// src/pages/AuditFormPage.jsx
import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  CheckCircle2,
  AlertCircle,
  X,
  Sparkles,
  TrendingUp,
  DollarSign,
  Clock,
  Users,
  Zap,
  ArrowRight,
  ShieldCheck,
  Mic,
  MicOff,
  Search,
  Check,
  Plus,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { LanguageContext } from "../App";
import { formatBudgetRange } from "../utils/reportHelpers";
import { captureAttribution, readAttribution } from "../utils/attribution";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  "https://ai-workshop-llc-backend.onrender.com";


// These are internal labels – you can localize how they render with t()
const INDUSTRY_NAMES = {
  retail: "Retail & E-commerce",
  hospitality: "Hospitality & Restaurants",
  professional_services: "Professional Services",
  technology: "Technology & SaaS",
  finance: "Accounting & Finance",
  manufacturing: "Manufacturing",
  real_estate: "Real Estate",
  property_management: "Property Management",
  education: "Education",
  dental: "Dental & Orthodontics",
  legal: "Legal",
  healthcare: "Healthcare & Medical",
  hvac: "HVAC (Heating & Cooling)",
  home_services: "Home Services (Plumbing, Electrical, Roofing, Pool)",
  accounting: "Accounting & Bookkeeping",
  ecommerce: "E-commerce & Shopify",
  construction: "Construction & Contracting",
  auto_repair: "Auto Repair & Mechanic",
  med_spa: "Med Spa, Salon & Beauty",
  credit_repair: "Credit Repair & Financial Services",
  other: "Other",
};

const INDUSTRY_ICONS = {
  retail: "🛍️",
  hospitality: "🍽️",
  professional_services: "💼",
  technology: "💻",
  finance: "💰",
  manufacturing: "🏭",
  real_estate: "🏠",
  property_management: "🏘️",
  education: "📚",
  dental: "🦷",
  legal: "⚖️",
  healthcare: "🏥",
  hvac: "❄️",
  home_services: "🛠️",
  accounting: "📊",
  ecommerce: "🛒",
  construction: "🏗️",
  auto_repair: "🚗",
  med_spa: "💆",
  credit_repair: "📊",
  other: "🔧",
};

// ---------- Industry-typical defaults for Step 2 ----------
// Applied when the user picks an industry on Step 1, so Step 2 lands with
// businessType and budget already pre-selected to what's most common for
// that industry. Reviewer's principle: "the form feels like you know them
// already instead of a blank interrogation." User can change anything in
// Step 2; this just removes 2 mandatory clicks for the typical buyer.
// QA caught: pre-selecting "$2K-$5K Essentials" by default over-anchors
// the typical 1-5-person buyer who has a $0-500/mo AI budget. Defaults
// now bias LOW (under-2k) so the form feels like "we know you're tight"
// rather than "we assume you have $5K to burn." Manufacturing stays
// higher because that buyer is genuinely larger. businessType defaults
// stay where they are — those are correct for the industry.
const INDUSTRY_DEFAULTS = {
  real_estate:           { businessType: "services",  budget: "under-2k" },
  property_management:   { businessType: "services",  budget: "under-2k" },
  dental:                { businessType: "services",  budget: "under-2k" },
  legal:                 { businessType: "services",  budget: "under-2k" },
  accounting:            { businessType: "services",  budget: "under-2k" },
  hvac:                  { businessType: "services",  budget: "under-2k" },
  home_services:         { businessType: "services",  budget: "under-2k" },
  professional_services: { businessType: "services",  budget: "under-2k" },
  healthcare:            { businessType: "services",  budget: "under-2k" },
  ecommerce:             { businessType: "ecommerce", budget: "under-500" },
  retail:                { businessType: "physical",  budget: "under-2k" },
  hospitality:           { businessType: "physical",  budget: "under-2k" },
  restaurants:           { businessType: "physical",  budget: "under-2k" },
  education:             { businessType: "services",  budget: "under-2k" },
  manufacturing:         { businessType: "both",      budget: "2k-5k"   },
  finance:               { businessType: "services",  budget: "5k-10k"  },
  technology:            { businessType: "services",  budget: "5k-10k"  },
  saas:                  { businessType: "ecommerce", budget: "5k-10k"  },
  construction:          { businessType: "services",  budget: "2k-5k"   },
  auto_repair:           { businessType: "services",  budget: "under-2k"},
  med_spa:               { businessType: "services",  budget: "2k-5k"   },
  credit_repair:         { businessType: "services",  budget: "2k-5k"   },
};

// ---------- Industry-aware accent palette ----------
// Subtle but signals "this form knows you." Once an industry is picked on
// Step 1, the Continue button + step-bar active dot + key accent moments
// shift to the industry's accent color. Default is the site's blue
// (#3b82f6) when no industry is selected yet. The palette is tuned to
// the existing landing-page accents so a buyer who came from /shopify
// sees a continuous visual story; a buyer who came from /hvac sees the
// orange/amber they expect.
//
// Each entry contains hex (for inline gradients/borders) and tw (a
// Tailwind-class fragment for backgrounds/text). Centralised here so
// future industries just add one row.
const INDUSTRY_ACCENT = {
  ecommerce:             { hex: "#0ea5e9", tw: "sky"     },  // sky-500
  retail:                { hex: "#0ea5e9", tw: "sky"     },
  shopify:               { hex: "#0ea5e9", tw: "sky"     },
  dental:                { hex: "#14b8a6", tw: "teal"    },  // teal-500
  healthcare:            { hex: "#14b8a6", tw: "teal"    },
  med_spa:               { hex: "#ec4899", tw: "pink"    },  // pink-500
  legal:                 { hex: "#6366f1", tw: "indigo"  },  // indigo-500
  accounting:            { hex: "#6366f1", tw: "indigo"  },
  finance:               { hex: "#6366f1", tw: "indigo"  },
  real_estate:           { hex: "#a855f7", tw: "purple"  },  // purple-500
  property_management:   { hex: "#10b981", tw: "emerald" },  // emerald-500 (matches /pm landing)
  hvac:                  { hex: "#f97316", tw: "orange"  },  // orange-500
  home_services:         { hex: "#f59e0b", tw: "amber"   },  // amber-500
  construction:          { hex: "#f59e0b", tw: "amber"   },
  auto_repair:           { hex: "#ef4444", tw: "red"     },  // red-500
  manufacturing:         { hex: "#0ea5e9", tw: "sky"     },
  technology:            { hex: "#3b82f6", tw: "blue"    },
  saas:                  { hex: "#3b82f6", tw: "blue"    },
  hospitality:           { hex: "#f59e0b", tw: "amber"   },
  restaurants:           { hex: "#f59e0b", tw: "amber"   },
  education:             { hex: "#10b981", tw: "emerald" },
  professional_services: { hex: "#3b82f6", tw: "blue"    },
  credit_repair:         { hex: "#6366f1", tw: "indigo"  },
};
const DEFAULT_ACCENT = { hex: "#3b82f6", tw: "blue" };

// ---------- Inline suggestion data (no API call needed) ----------
// Rich, stat-backed suggestions served instantly — no cold-start latency.

const INDUSTRY_SUGGESTIONS = {
  retail: {
    // 18 problems — first 6 are the highest-leverage chips shown by default,
    // remaining 12 surfaced via "Show more". Sourced from PROBLEM_NODES
    // (cart_abandonment_01, inventory_guesswork_01, checkout_friction_01,
    // poor_product_reviews_01, customer_churn_retail_01, low_aov_retail_01,
    // payment_failure_01, high_return_rate_01, pricing_pressure_01) plus
    // industry-vernacular pains operators actually voice on threads/forums.
    problems: [
      "High cart abandonment (70%+ costs $250–400 daily)",
      "Low repeat purchase rate (27% industry avg)",
      "Inventory forecasting gaps (stockouts cost 4.1% of revenue)",
      "Personalization at scale (AI boosts AOV 22–35%)",
      "Price optimization not in place (dynamic pricing lifts margins 8–15%)",
      "No unified view of which campaigns actually drive revenue",
      "Checkout friction — too many steps before customers convert",
      "Payment failures at checkout (3–7% of attempted transactions)",
      "High return rate eating margin (processing + restocking)",
      "Slow product page load on mobile (each second = 7% conversion drop)",
      "Customer support tickets piling up — same questions over and over",
      "Product reviews are sparse — no automated review request flow",
      "SEO/paid ad attribution unclear — can't tell what's profitable",
      "Loyalty program is manual or nonexistent",
      "Email and SMS campaigns are one-size-fits-all (low engagement)",
      "Fraud chargebacks rising — manual review is too slow",
      "Supplier delays force last-minute pricing/availability changes",
      "Social content production is a weekly bottleneck",
    ],
    goals: [
      "Recover more abandoned carts automatically (AI recovers 18–27%)",
      "Increase repeat purchases by 20–35%",
      "Improve inventory accuracy and cut stockouts",
      "Boost AOV with smarter product recommendations",
      "Automate promotions, email, and SMS campaigns",
      "Get a clear weekly view of real revenue drivers",
      "Cut customer support tickets 50–60% with an AI agent that handles FAQs",
      "Automate review request flows to grow Google/site rating",
      "Build segmented email + SMS journeys per customer behavior",
      "Detect fraud chargebacks before they ship",
      "Optimize ad spend with AI-driven attribution per channel",
      "Generate product copy + social content from a single brief",
    ],
    currentTools: [
      "Shopify / WooCommerce", "Klaviyo / Mailchimp", "QuickBooks / Xero",
      "Google Analytics / GA4", "HubSpot / CRM", "Square / POS",
      "Zendesk / Gorgias", "Lightspeed / BigCommerce",
      "Yotpo / Loox / Stamped (reviews)", "Recharge / Bold (subscriptions)",
      "Postscript / Attentive (SMS)", "Privy / Justuno (popups)",
      "Smile.io / Yotpo Loyalty", "ShipStation / Shippo",
      "Triple Whale / Northbeam (attribution)", "Meta Ads / Google Ads",
      "Slack / Teams", "Zapier / Make",
    ],
  },
  hospitality: {
    // Sourced from PROBLEM_NODES: no_shows_restaurant_01, food_waste_01,
    // staff_scheduling_hosp_01, online_reviews_01, upselling_failure_01,
    // table_turnover_01, seasonal_staffing_01, beverage_costs_01.
    problems: [
      "High no‑show rate (17.8% avg — costs $400–600 nightly)",
      "Labor scheduling inefficiencies eating 34% of revenue",
      "Manual reservation management overwhelming front‑of‑house",
      "Guest personalization not in place (check size up 21–34% with AI)",
      "Dynamic pricing not implemented (RevPAR lift 8–15% possible)",
      "Staff time lost on repetitive phone and email questions",
      "Food waste eating 4–8% of revenue (no portion/inventory AI)",
      "Online review response time is too slow (Yelp/Google sit unanswered)",
      "Online ordering fragmented across DoorDash/Uber/native — margins crushed",
      "Table turnover slower than industry — peak hours leave money on the floor",
      "Seasonal staffing crunch — can't scale up/down without overstaffing",
      "Beverage costs creeping up — no real-time variance tracking",
      "Upselling falls apart on busy nights (servers default to entrees only)",
      "Kitchen-to-front comms break down at peak (delays, comp meals)",
      "Voicemails for reservations pile up — guests pick the next restaurant",
      "Gift cards / vouchers tracked manually (revenue leaks via expired/duplicate)",
      "Holiday surge planning is reactive — overcommit then scramble",
      "Employee turnover at 75%+ — retraining costs eat margin",
    ],
    goals: [
      "Reduce no‑shows by 30–55% with automated reminders",
      "Optimize labor costs and scheduling (save 18–26%)",
      "Increase online bookings 12–19% with AI chat",
      "Improve guest review scores via sentiment analysis",
      "Automate reservations and FAQs 24/7",
      "Speed up check‑in/check‑out workflows",
      "Cut food waste 20–30% with AI portion + inventory forecasting",
      "Automated review responses within 1 hour of every Yelp/Google post",
      "Unified online-ordering dashboard across DoorDash + Uber + native",
      "AI-driven dynamic pricing for off-peak slot recovery",
      "Smart upsell prompts at the POS (raise check size $4–9 per guest)",
      "Build a recurring-guest loyalty flow that fills slow nights",
    ],
    currentTools: [
      "Toast / Square / Clover", "OpenTable / Resy / Tock", "QuickBooks / Xero",
      "7shifts / HotSchedules / Sling", "Guesty / Cloudbeds / Mews",
      "Yelp / Google Business", "Revinate / ReviewPro",
      "DoorDash / Uber Eats / Grubhub", "Olo / ChowNow",
      "BentoBox / Slice (online ordering)", "SevenRooms (reservations + CRM)",
      "Mailchimp / Klaviyo", "Canva / Figma",
      "Slack / Teams", "Zapier / Make",
      "Square Loyalty / Toast Loyalty",
      "Hotjar / Google Analytics",
    ],
  },
  professional_services: {
    // Sourced from PROBLEM_NODES: lead_response_slow_01, manual_data_entry_01,
    // proposal_creation_01, client_onboarding_01, utilization_tracking_01,
    // billing_collections_01, scope_creep_01, client_reporting_01,
    // contract_review_01.
    problems: [
      "Client intake paperwork consuming 15+ hours/week",
      "Proposal generation taking too long (losing deals to faster competitors)",
      "Manual invoicing and billing errors costing 15–20% revenue leakage",
      "Client communication gaps (slow response times)",
      "Document collection delays slowing onboarding",
      "Time tracking inaccuracy across the team",
      "Lead response time too slow — leads cool before someone calls back",
      "Project utilization unclear — can't tell who's underused vs overloaded",
      "Scope creep eats margin on most engagements",
      "Client status reports take half a day each cycle",
      "Contract review is a partner-only bottleneck (NDAs, MSAs queue up)",
      "Status update questions repeat in email — same answer, different client",
      "Knowledge management gaps — playbooks live in one or two heads",
      "Junior associate onboarding = months of shadowing",
      "Project profitability is a back-office mystery until close",
      "RFP responses miss deadlines or come out generic",
      "Mid-quarter pipeline gaps go unnoticed until billings drop",
      "Marketing content production stalls every quarter",
    ],
    goals: [
      "Automate client intake and save 15+ hours/week",
      "Generate proposals 3x faster without losing quality",
      "Improve billing accuracy and reduce revenue leakage",
      "24/7 client communication via AI assistant",
      "Faster document processing and e-signature workflows",
      "Client retention analytics and proactive outreach",
      "Real-time utilization dashboard so capacity is visible weekly",
      "Catch scope creep with AI-monitored project burn rate",
      "Generate weekly client status reports automatically",
      "AI-assisted contract review for routine NDAs/MSAs/SOWs",
      "Build a searchable firm knowledge base for templates + playbooks",
      "Pipeline + billings forecast updated daily, not at month-end",
    ],
    currentTools: [
      "Clio / MyCase / Karbon", "QuickBooks / Xero", "Salesforce / HubSpot / Pipedrive",
      "DocuSign / PandaDoc / HelloSign", "Zoom / Teams / Loom",
      "Asana / Monday / ClickUp / Linear",
      "Calendly / Acuity / SavvyCal",
      "Notion / Airtable / Coda", "Slack / Teams",
      "Mercury / Stripe / Brex",
      "Loom / Vidyard (video updates)",
      "Gmail / Outlook", "Zapier / Make",
      "Mixpanel / Amplitude (product analytics)",
      "Google Analytics / GA4",
      "Mailchimp / Customer.io",
      "Lattice / 15Five (people ops)",
    ],
  },
  technology: {
    problems: [
      "Customer churn too high (18.5% annual rate)",
      "Developer productivity gaps (2–3x lift possible with AI code tools)",
      "Technical debt consuming 55% of dev time",
      "Customer acquisition cost too high ($420 average)",
      "Feature prioritization inaccurate — shipping the wrong things",
      "Burn rate pressure with no clear AI leverage plan",
      "Trial-to-paid conversion stuck under 18%",
      "Support ticket volume rising faster than headcount",
      "Onboarding drop-off in the first 7 days kills activation",
      "Time-to-first-value over 14 days (industry leaders hit 3–5)",
      "NPS surveys collected but never actioned",
      "Feature requests buried in Slack threads + emails",
      "Sales-led vs PLG signals mixed — go-to-market unclear",
      "Code review backlog blocks shipping each sprint",
      "Sprint estimation accuracy off 40%+ — quarter slips",
      "Customer success can't scale 1:1 — needs automation",
      "Content marketing for SEO is sporadic at best",
      "DevRel/community is a side project, not a function",
    ],
    goals: [
      "Reduce churn 25–40% with predictive AI",
      "2–3x developer velocity with AI coding tools",
      "Lower CAC 20–35% with better targeting",
      "Personalized onboarding to improve activation and retention",
      "Automated code review and quality gates",
      "Customer success automation at scale",
      "Lift trial-to-paid conversion 30–50% with usage-driven nudges",
      "Cut support ticket volume 40%+ with AI deflection",
      "Cut time-to-first-value to under 5 days for new accounts",
      "Build a predictive churn-risk model using product usage signals",
      "Auto-generate release notes + customer-facing changelog",
      "Pipe NPS responses into a per-team weekly action queue",
    ],
    currentTools: [
      "Jira / Linear / Shortcut", "Salesforce / HubSpot / Pipedrive",
      "GitHub / GitLab / Bitbucket", "Slack / Teams",
      "QuickBooks / Xero", "VS Code / Cursor / Codeium",
      "Figma / Miro / FigJam", "Notion / Confluence / Coda",
      "Mixpanel / Amplitude / Heap (product analytics)",
      "Sentry / Datadog / New Relic (observability)",
      "Stripe / Chargebee / Recurly (billing)",
      "Vercel / Netlify / Cloudflare",
      "Auth0 / Clerk / Supabase",
      "Customer.io / Loops / Mailchimp",
      "Productboard / Canny (feedback)",
      "Baremetrics / ChartMogul / ProfitWell (subscription metrics)",
      "Intercom / Front (customer support)",
      "AWS / GCP / Azure",
    ],
  },
  finance: {
    // PROBLEM_NODES sources: late_invoice_payments_01, expense_management_01,
    // tax_prep_chaos_01, payroll_errors_01, fraud_detection_01.
    problems: [
      "Tax season overwhelm — client docs arrive late, prep takes forever",
      "Manual data entry from bank statements and receipts (5–10 hrs/week)",
      "Client onboarding paperwork taking 3–5 days per new client",
      "Reconciliation errors catching up to us every month-end",
      "Clients asking the same questions over and over (status updates, deadlines)",
      "Advisory services we'd love to offer but no time for",
      "Late invoice payments — 60–90 day A/R aging is normal",
      "Expense receipts scattered across email, photos, paper",
      "Payroll errors require corrected runs (tax forms, W-2/1099 amendments)",
      "Fraud detection is reactive — we catch it on the bank reconciliation",
      "1099/W2 prep at year-end is a manual scramble",
      "Client portal complaints — they can't find their docs or invoices",
      "Audit trail gaps — hard to prove who approved what when",
      "Software switching projects (Xero → QBO, etc) take months",
      "Cross-state compliance — sales tax + nexus rules keep changing",
      "Trust account reconciliation done manually — high stakes if off",
      "Billable-hour leakage on email/Slack threads with clients",
      "Fee disputes drain partner time monthly",
    ],
    goals: [
      "Cut data entry time 70–80% with AI extraction from receipts and statements",
      "Client onboarding in 24 hours instead of 5 days",
      "Automated reconciliation — catch mismatches before month-end",
      "AI-assisted tax prep that flags common deductions we miss",
      "Client portal with self-serve answers to routine questions",
      "Free up 10+ hrs/week for high-value advisory work",
      "Cut A/R aging to under 30 days with automated reminder cadence",
      "AI fraud detection running on every reconciliation",
      "Auto-generate 1099/W2/K-1 from connected payroll + bank data",
      "Recovered billable hours via auto-tracked email/Slack work",
      "Single source of truth for trust + operating + payroll accounts",
      "Predictive cash-flow forecasting per client by week",
    ],
    currentTools: [
      "QuickBooks / Xero / Sage", "Drake / Lacerte / ProConnect / UltraTax",
      "Bill.com / Melio / Ramp", "Gusto / ADP / Rippling / Paychex",
      "Dext / Hubdoc / AutoEntry", "Loom / Zoom",
      "Karbon / Jetpack Workflow / Canopy / TaxDome",
      "Avalara / TaxJar (sales tax)",
      "Trolley / Track1099 (1099 prep)",
      "FreshBooks / Wave / Zoho Books",
      "ADP / Rippling / Justworks (PEO)",
      "Mercury / Stripe / Plaid (banking)",
      "Slack / Teams", "Calendly / Acuity",
      "DocuSign / HelloSign", "Mailchimp / ActiveCampaign",
      "Ignition / Practice Ignition (proposals)",
    ],
  },
  manufacturing: {
    // PROBLEM_NODES: equipment_downtime_01, quality_defects_01,
    // production_planning_01, supplier_reliability_01, labor_productivity_01,
    // energy_costs_01.
    problems: [
      "Unplanned equipment downtime (12.4% avg)",
      "Quality control defect rate too high (4.8% avg)",
      "Supply chain volatility and cost overruns",
      "Inventory carrying costs eating margin",
      "Energy costs at 7.1% of revenue — room to optimize",
      "Predictive maintenance not in place",
      "Production planning still done in spreadsheets (overruns weekly)",
      "Supplier reliability swings — late deliveries hit 18%+ of orders",
      "Labor productivity uneven across shifts",
      "Shop-floor visibility limited — owner walks the floor for status",
      "Work orders printed and lost mid-shift",
      "OEE (overall equipment effectiveness) tracked monthly, not real-time",
      "Training on new SKUs slow — first-pass yield drops on intro",
      "Customer order changes ripple through scheduling chaos",
      "Batch documentation manual — quality audits eat days",
      "Traceability across raw → finished goods is partially manual",
      "Rework rate climbing — root causes not investigated systematically",
      "Environmental compliance reporting is a 4x/year fire drill",
    ],
    goals: [
      "Reduce downtime 30–50% with predictive maintenance",
      "Cut defect rate 40% with AI quality control",
      "Supply chain costs down 15–25%",
      "Inventory carrying costs down 10–15%",
      "Energy efficiency improvement 15–25%",
      "Production throughput optimization",
      "Real-time OEE dashboard visible on every shift",
      "AI scheduling that absorbs customer order changes within an hour",
      "Digital work orders and traveler sheets — no paper on the floor",
      "Automated supplier scorecards and re-order point alerts",
      "Cut first-pass yield drop on new SKUs 50%+ via AI training assist",
      "Compliance docs auto-assembled per audit window",
    ],
    currentTools: [
      "SAP / Oracle / NetSuite", "QuickBooks / Xero / Sage",
      "Fishbowl / Katana / Cin7", "MRPeasy / Odoo / Acumatica",
      "Tableau / Power BI / Looker", "Siemens / Rockwell (industrial automation)",
      "Microsoft Dynamics / Plex / Epicor / Infor",
      "MachineMetrics / Plant24 (OEE tracking)",
      "Kepware / OPC servers (PLC connectivity)",
      "ShopFloorIQ (digital work orders)",
      "Procore (capital projects)", "Slack / Teams",
      "Salesforce / HubSpot (B2B CRM)",
      "Bill.com / Melio (AP)",
      "ADP / Paychex (payroll)",
      "Microsoft 365 / Google Workspace",
      "Zapier / Make / n8n",
      "Excel / Google Sheets",
    ],
  },
  real_estate: {
    // PROBLEM_NODES: lead_follow_up_re_01, listing_syndication_01,
    // tenant_screening_01, maintenance_chaos_01, lease_renewal_01.
    problems: [
      "Slow lead response (38‑hour avg — AI cuts to under 5 min)",
      "Comps and CMAs eating 30+ min each listing",
      "Listing descriptions and photos taking hours per property",
      "Transaction coordination juggling 40–60 tasks per deal",
      "Sphere‑of‑influence follow‑up falls through the cracks",
      "Days on market too high (52 days avg)",
      "Lead scoring and nurture is manual and inconsistent",
      "Client retention low (62% rate) — past clients forgotten",
      "Listings out of sync across MLS / Zillow / Realtor.com / brokerage",
      "Tenant screening (PM) takes 5+ days per applicant",
      "Maintenance requests pile up — vendor coordination is manual",
      "Lease renewal outreach is reactive — tenants leave without warning",
      "Open house follow-up sits in inboxes after Sunday",
      "Agent commission tracking is a spreadsheet nightmare",
      "Escrow timeline tracking — milestones slip without alerts",
      "Vendor coordination (inspector, photographer, stager) eats hours",
      "Broker compliance docs (state filings) miss deadlines",
      "Recruiting + training new agents is a one-on-one drag",
    ],
    goals: [
      "Lead response under 5 minutes (vs 38‑hour avg)",
      "CMAs and comps generated in minutes, not 30+",
      "AI‑drafted listing copy + virtual staging at scale",
      "Transaction coordination automated end‑to‑end",
      "SOI follow‑up on autopilot (quarterly check‑ins, birthdays, home anniversaries)",
      "AI lead scoring and nurture so cold leads warm themselves",
      "Days on market cut 20–35% with AI pricing",
      "Keep past clients close with automated value‑add touches",
      "Tenant screening turnaround under 24 hours",
      "Automated maintenance ticketing — vendor dispatched + status tracked",
      "Lease renewal outreach starts 90 days out, automatically",
      "Listings sync across MLS + portals + site within minutes of update",
    ],
    currentTools: [
      "MLS / Rapattoni / FlexMLS", "Follow Up Boss / Lofty / Real Geeks / Chime",
      "DocuSign / Dotloop / SkySlope", "kvCORE / BoomTown / Sierra Interactive",
      "Canva / Matterport / VirtualAPT", "QuickBooks / Xero",
      "Salesforce / HubSpot", "AppFolio / Buildium / Yardi (PM)",
      "BombBomb / BombBomb Prompt / Vyond (video)",
      "Zillow Premier Agent / StreetText (lead gen)",
      "kw Command / Sierra (kw / coldwell platforms)",
      "Showcase IDX / Showit (websites)",
      "BoomTown PowerSplash / Ylopo / REW",
      "Mailchimp / ActiveCampaign",
      "Brokermint / Realvolve (back office)",
      "Slack / Teams", "Zapier / Make",
      "ChatGPT / Claude / Gemini",
    ],
  },
  // R35 (Sarah verdict, P0): dedicated property-management chip pool.
  // Sarah's R35 walk: a buyer arriving via /pm with sub_vertical=
  // property_management was getting GENERIC SMB chips (bookkeeping,
  // hiring, social media) — bait-and-switch from the FCRA-themed ad.
  // The real_estate pool above mixes brokerage + PM chips; this pool
  // is PM-only. The chip-pool selector in getSuggestions() routes to
  // this entry when subVertical === 'property_management', overriding
  // the real_estate fallback.
  //
  // Chip set covers: tenant screening (FCRA), maintenance dispatch,
  // lease lifecycle, vacancy, owner reporting, fair-housing, eviction
  // compliance, application volume, vendor coordination, multi-platform
  // sprawl, late-rent escalation, owner communication. 18 chips so the
  // 5-of-18 refresh cycle has variety.
  property_management: {
    problems: [
      "Tenant screening takes 5+ days per applicant — best applicants sign elsewhere",
      "Maintenance requests scatter across texts, voicemail, and Instagram DMs",
      "Lease renewal outreach is reactive — tenants give notice before we open the conversation",
      "Vacancy days running 30+ per turnover — every empty unit is $1,500–3,000 lost",
      "Owner monthly reports eat 4+ hours per property — manual NOI / vacancy / spend pulls",
      "FCRA / fair-housing anxiety — afraid to use AI tools that could trigger an adverse-action lawsuit",
      "Application volume overwhelming — can't qualify the pile fast enough during busy season",
      "Eviction process compliance scares us — different rules per jurisdiction, mistakes are costly",
      "Vendor coordination (plumber, electrician, locksmith, cleaner) is manual texts",
      "Late-rent chases bouncing between calls, texts, email — inconsistent escalation",
      "Tenant communication scattered — no single inbox for the portfolio",
      "Multi-platform sprawl (AppFolio + RentSpree + DocuSign + 5 spreadsheets)",
      "Owner communication burnout — same questions asked across phone, email, text",
      "Move-out / make-ready coordination is 12 calls and 30 texts per turnover",
      "Inspection scheduling + photographer coordination eats Friday afternoons",
      "Rent collection escalation (3-day notice → 30-day notice) is manual + error-prone",
      "Pet / amenity / parking add-on tracking lives in 3 different spreadsheets",
      "Year-end 1099-MISC owner statements + tax docs are a 2-week scramble",
    ],
    goals: [
      "FCRA-compliant tenant screening end-to-end in under 24 hours",
      "Maintenance ticket auto-routing to the right vendor with SLA tracking",
      "Lease renewals nurtured 60–90 days out automatically — anniversary, market update, renewal offer",
      "Vacancy days cut to under 10 — auto-listing, faster screening, AI applicant intake",
      "Owner reports auto-generated in 10 minutes — branded PDF pulled from AppFolio/Buildium",
      "Adverse-action notices auto-drafted with §615 timing compliance",
      "Application volume triaged by AI — fit-score before human review",
      "One inbox for tenant communication — phone + text + email unified",
      "Vendor coordination via single ticket queue — schedule, dispatch, status, invoice",
      "Late-rent escalation runs on rails — 3-day notice → 10-day → eviction filing",
      "Multi-jurisdiction eviction compliance flagged + tracked per property",
      "Move-out + make-ready coordinated automatically — inspection, repairs, listing live",
      "Owner Q&A auto-handled by AI — FAQ + portal access for routine asks",
      "Year-end owner statements + 1099s auto-prepared from accounting integration",
      "Rent collection cadence standardized + tracked — inconsistencies surface as alerts",
    ],
    currentTools: [
      "AppFolio", "Buildium", "Yardi", "Rent Manager", "Rentvine", "DoorLoop",
      "RentSpree", "TurboTenant", "Avail", "RentRedi", "TransUnion SmartMove",
      "DocuSign", "QuickBooks / Xero", "Stripe / ACH",
      "Zillow Rental Manager", "Apartments.com", "Realtor.com",
      "Mailchimp / ActiveCampaign",
      "ServiceTitan / Housecall Pro (vendor side)",
      "Slack / Teams", "Zapier / Make",
      "ChatGPT / Claude / Gemini",
      "Google Workspace / Microsoft 365",
    ],
  },
  education: {
    problems: [
      "Student engagement low (58% rate)",
      "Dropout rates too high (18.4% avg)",
      "Assessment grading consuming 12 hours/week per teacher",
      "Lack of personalized learning paths",
      "Administrative burden (30% of staff time non‑instructional)",
      "Parent communication is inconsistent and slow",
      "Attendance tracking + interventions are reactive",
      "IEP/504 plan tracking and parent reporting eats hours",
      "Lesson planning takes evenings — no shared library",
      "Professional development scheduling is a calendar puzzle",
      "Library/resource discovery slow — students give up",
      "Fundraising/development outreach is manual + inconsistent",
      "Alumni engagement drops to zero after graduation",
      "Faculty hiring cycle takes 3–6 months",
      "Accreditation prep is a 6-month all-hands fire drill",
      "Special-needs support coordination falls between roles",
      "Summer program planning starts too late every year",
      "Student transitions (grade-up, school-change) info gets lost",
    ],
    goals: [
      "Increase engagement 25–40% with personalization",
      "Reduce dropout rates with predictive intervention",
      "Cut grading time 70–85% with AI assessment",
      "Adaptive learning paths for every student",
      "24/7 student support chatbots",
      "Administrative task automation",
      "Auto-generate IEP progress reports for parents",
      "Shared lesson-plan library + AI-assisted differentiation",
      "Predictive attendance alerts for at-risk patterns",
      "Year-round alumni engagement on autopilot",
      "Faster faculty hiring cycle with AI screening",
      "Always-current accreditation documentation",
    ],
    currentTools: [
      "Canvas / Blackboard / Brightspace / D2L",
      "PowerSchool / Infinite Campus / Skyward",
      "Google Classroom / Microsoft Teams Education",
      "Zoom / Teams / Google Meet",
      "Schoology / Seesaw / ClassDojo",
      "Nearpod / Kahoot / Quizizz / Edpuzzle",
      "QuickBooks / Xero",
      "Naviance (college + career planning)",
      "PowerTeacher Gradebook / Aeries Gradebook",
      "Loom / Flipgrid / Padlet",
      "Khan Academy / Coursera / Quizlet",
      "Mailchimp / Constant Contact (parent comm)",
      "PaperRater / Grammarly (writing assist)",
      "Slack / Teams", "Zapier / Make",
      "Salesforce Education Cloud / Slate (admissions)",
      "Bonusly / Sown to Grow (engagement)",
      "Microsoft 365 / Google Workspace",
    ],
  },
  dental: {
    // Sourced from PROBLEM_NODES (healthcare): patient_no_shows_01,
    // billing_delays_01, staff_documentation_01, patient_communication_01,
    // last_minute_cancellations_01, insurance_verification_01,
    // patient_satisfaction_01.
    problems: [
      "No-shows and last-minute cancellations costing $400–800/day",
      "Front desk overwhelmed — phone ringing nonstop, patients on hold",
      "Insurance verification eating 2–3 hours every morning",
      "Case acceptance rate stuck at 40–50% (should be 70%+)",
      "Patient recall and reactivation is manual — we lose patients silently",
      "Marketing spend with no idea what's actually bringing in new patients",
      "Hygiene re-care rate below 60% — patients drift to other practices",
      "Treatment plan dropoffs after consult (financing not offered upfront)",
      "Lab integration delays — crowns/dentures take longer than promised",
      "X-ray AI not used — caries and bone-loss flags missed routinely",
      "Online review acquisition is sporadic (Google rating sits at 4.2 vs 4.7+)",
      "Patient financing options not surfaced at presentation",
      "Family communication for pediatric patients is fragmented",
      "Voicemails returned in batches at end-of-day — not all-day responsive",
      "End-of-day balance reconciliation eats 30+ minutes nightly",
      "Supply ordering (gloves, anesthetic) hits stockouts unexpectedly",
      "After-hours emergency calls go to voicemail half the time",
      "Patient education on procedures relies on chairside explanation only",
    ],
    goals: [
      "Cut no-shows 40–60% with automated reminders and confirmations",
      "AI answering service for after-hours calls and scheduling",
      "Speed up insurance verification from hours to minutes",
      "Increase case acceptance with AI-generated treatment visualizations",
      "Automated recall campaigns that bring back inactive patients",
      "Know exactly which marketing channels produce new patient bookings",
      "Hygiene re-care rate above 75% via automated 6-month outreach",
      "AI x-ray review flagging caries/bone loss in real time chairside",
      "Lift Google rating to 4.7+ with automated post-visit review requests",
      "Pre-approve patient financing at consult (not after they leave)",
      "Reduce front-desk admin time 40%+ with AI scheduling + confirmations",
      "Cut insurance claim denials with AI coding + eligibility verification",
    ],
    currentTools: [
      "Dentrix / Eaglesoft / Open Dental / Curve Dental",
      "Weave / Podium / NexHealth (patient comm)",
      "RevenueWell / Lighthouse / Modento (recall + reactivation)",
      "Yapi / OperaDDS (intra-office comm)",
      "Dental Intelligence / Practice by Numbers (analytics)",
      "DentalCare.com / DentalIntel (benchmarking)",
      "QuickBooks / Xero",
      "Google Business / Yelp",
      "Mailchimp / Constant Contact",
      "DemandForce / Solutionreach",
      "Dexis / Sirona / Planmeca (imaging)",
      "DEXIS IS 3800 / Trios / Medit (intraoral scanners)",
      "Pearl / VideaHealth (AI radiology)",
      "Sunbit / CareCredit / Proceed Finance (financing)",
      "DentalPlans / DentalPostJobs (recruiting)",
      "Calendly / NexHealth (online booking)",
      "Slack / Teams", "Zapier / Make",
    ],
  },
  legal: {
    // PROBLEM_NODES: client_intake_legal_01, document_review_legal_01,
    // time_tracking_legal_01, compliance_changes_01.
    problems: [
      "Document review taking 10+ hours per matter — most is routine",
      "Client intake bottleneck — inquiries sit unanswered for days",
      "Billable hours lost to admin (emails, scheduling, filing)",
      "Conflict checks are manual and terrify us on the liability front",
      "Knowledge trapped in partners' heads — junior associates reinvent the wheel",
      "Matter deadlines tracked in spreadsheets — we've had close calls",
      "Regulatory changes go untracked until a client points them out",
      "Voicemails return in 24+ hours — competitors with answering services beat us",
      "Court filing prep eats associate time on formatting + cite-checking",
      "Witness coordination involves 20+ emails per deposition",
      "Deposition prep takes a week per case — same patterns repeat",
      "Exhibit organization is a manual paper-and-Bates-stamp slog",
      "Citation checking and Bluebooking still done line-by-line",
      "Fee disputes with clients require partner mediation each month",
      "Retainer replenishment chasing is a quarterly billing crisis",
      "IOLTA reconciliation done manually — bar audit risk if off",
      "Business development time eats into billable capacity",
      "Marketing content production for SEO is completely sporadic",
    ],
    goals: [
      "AI-assisted document review cutting 60–70% of routine review time",
      "Instant client intake screening — qualify leads while they're still on the site",
      "Recapture 5–8 billable hours/week by automating admin tasks",
      "Automated conflict checks that run in seconds, not hours",
      "Firm knowledge base so associates find precedents and templates instantly",
      "Never miss a deadline — automated calendar and reminder system",
      "Auto-track regulatory changes affecting active matters",
      "AI-drafted deposition prep memos and witness outlines",
      "AI cite-checker + formatter for filings (cuts associate time 50–70%)",
      "Automatic retainer replenishment alerts before threshold",
      "Track all email/Slack work back to a matter for true billable capture",
      "AI-generated SEO content for practice-area landing pages",
    ],
    currentTools: [
      "Clio / MyCase / PracticePanther / Smokeball",
      "Westlaw / LexisNexis / Bloomberg Law / Casetext",
      "DocuSign / HelloSign / Adobe Sign",
      "QuickBooks / Xero / TimeSolv",
      "Outlook / Gmail",
      "Dropbox / NetDocuments / iManage / SharePoint",
      "Lawmatics / Lexicata (intake)",
      "Filevine / CASEPeer (case management)",
      "TrialPad / TrialDirector (trial prep)",
      "Calendly / Acuity",
      "Microsoft Word + Google Docs",
      "Slack / Teams",
      "Mailchimp / ActiveCampaign",
      "Loom / Zoom",
      "PandaDoc / Proposify",
      "Spellex / WordPerfect (legal-specific writing)",
      "Litera / iManage (drafting + DMS)",
      "Zapier / Make",
    ],
  },
  healthcare: {
    // PROBLEM_NODES: patient_no_shows_01, billing_delays_01,
    // staff_documentation_01, patient_communication_01,
    // last_minute_cancellations_01, insurance_verification_01,
    // patient_satisfaction_01.
    problems: [
      "Appointment no-shows running 15–20% (costs $200+ per empty slot)",
      "Clinical documentation eating 2+ hours of provider time daily",
      "Patient intake forms are still paper or clunky PDFs",
      "Billing and coding errors causing claim denials (5–10% denial rate)",
      "Patient follow-up after visits is inconsistent or nonexistent",
      "Staff burnout from repetitive admin work",
      "Insurance pre-authorization delays push appointments out by weeks",
      "Last-minute cancellations leave gaps we can't fill same-day",
      "Patient satisfaction scores trending down — recovery is reactive",
      "Prior-auth workflows manual — staff on hold with payers daily",
      "Referral coordination drops between practices (fax + phone)",
      "Telehealth no-shows higher than in-person (no automated reminders)",
      "Patient education content hand-printed or non-existent",
      "Lab result delivery takes 3–7 days when patients want same-day",
      "Prescription refill workload eats half of MA day",
      "MIPS / quality reporting is a year-end fire drill",
      "Credentialing renewals slip — claims rejected during gap",
      "Equipment maintenance + supply ordering reactive, not predictive",
    ],
    goals: [
      "Reduce no-shows to under 5% with smart reminders and waitlist fills",
      "AI-assisted clinical notes — cut documentation time in half",
      "Digital intake that auto-populates into the EHR",
      "Reduce claim denials 40–60% with AI coding suggestions",
      "Automated post-visit follow-up and patient satisfaction surveys",
      "Free up clinical staff from admin so they focus on patient care",
      "AI-driven prior-auth submission and status tracking",
      "Same-day waitlist fills via SMS when cancellations happen",
      "Auto-generated patient education materials per visit type",
      "Real-time eligibility verification at check-in",
      "Continuous MIPS quality tracking instead of year-end scramble",
      "Predictive supply ordering tied to appointment volume",
    ],
    currentTools: [
      "Epic / Athenahealth / DrChrono / NextGen / eClinicalWorks",
      "Kareo / AdvancedMD / Practice Fusion",
      "Allscripts / Cerner / Greenway",
      "QuickBooks / Xero",
      "Zoom / Doxy.me / Updox (telehealth)",
      "Phreesia / IntakeQ / Healow (digital intake)",
      "Mailchimp / Klara / Spruce Health (patient comm)",
      "SimplePractice / TheraNest / Kalix (behavioral health)",
      "Modernizing Medicine / RXNT",
      "Bridge Patient Portal / FollowMyHealth",
      "Wellbox / TimeDoc (RPM/CCM)",
      "Office Ally / Trizetto (clearinghouse)",
      "Salesforce Health Cloud / Caspio",
      "Slack / Teams", "Calendly / NexHealth",
      "Loom / Zoom", "Zapier / Make",
      "Microsoft 365 / Google Workspace (HIPAA-compliant)",
    ],
  },
  hvac: {
    // PROBLEM_NODES: hvac_missed_calls_01, hvac_slow_estimate_01,
    // hvac_dispatch_overhead_01, hvac_seasonal_capacity_01, hvac_followup_01.
    problems: [
      "Missed calls = lost jobs — can't answer the phone while on a roof",
      "Scheduling chaos — double bookings, drive time wasted, no route optimization",
      "Quoting takes too long — customers get 3 quotes and we're last",
      "No follow-up after service — losing repeat and maintenance contract revenue",
      "Techs filling out paperwork instead of turning wrenches",
      "Seasonal feast-or-famine cash flow swings",
      "Dispatch overhead — owner spends 90 min/morning routing techs",
      "Parts inventory mistakes (truck stocked wrong → return trip)",
      "Warranty tracking manual — service contracts slip the cracks",
      "Recurring maintenance contracts not auto-renewed",
      "Recruiting service techs takes months — labor market is tight",
      "Training new techs is shadow-the-veteran (slow ramp)",
      "Customer review acquisition sporadic — Google rating undersold",
      "After-hours emergency triage falls to owner's personal phone",
      "HVAC + plumbing + electrical handoffs lose context (cross-utility)",
      "Fleet vehicle tracking + fuel costs unmonitored",
      "Certification compliance (EPA 608, etc.) tracked in spreadsheets",
      "Social media for service area marketing is dormant",
    ],
    goals: [
      "AI answering service that books calls even when we're on the job",
      "Smart scheduling with route optimization — cut drive time 20–30%",
      "Generate quotes in minutes, not hours — win more bids",
      "Automated maintenance reminders that fill the slow season pipeline",
      "Digital work orders and invoicing from the truck — no more lost paperwork",
      "Predictable revenue with automated service agreement renewals",
      "AI dispatcher that handles morning routing in 10 min, not 90",
      "Truck inventory tied to job calendar — right parts loaded automatically",
      "Auto-renewing maintenance contracts with prepay incentives",
      "Tech training portal with searchable troubleshooting playbooks",
      "Auto-request reviews after every completed service call",
      "Off-season campaign engine that fills January–February pipeline",
    ],
    currentTools: [
      "ServiceTitan / Housecall Pro / Jobber / FieldEdge",
      "BuildOps / ServiceFusion / mHelpDesk",
      "Workiz / Tradify / Service Autopilot",
      "QuickBooks / Xero",
      "Google Business / Yelp / Angi",
      "Thumbtack / HomeAdvisor / Networx",
      "Mailchimp / Constant Contact (recall)",
      "CompanyCam (jobsite photos)",
      "Podium / Birdeye (review acquisition)",
      "Spreadsheets / Google Sheets",
      "Square / Stripe (mobile payments)",
      "FleetSharp / Verizon Connect (vehicle tracking)",
      "Slack / Teams (crew comms)",
      "Calendly / Acuity (consults)",
      "DocuSign / Jotform (estimates / contracts)",
      "Zapier / Make",
      "Voice / SMS (RingCentral / OpenPhone)",
      "ChatGPT / Claude / Gemini",
    ],
  },
  home_services: {
    // PROBLEM_NODES: hs_emergency_response_time_01, hs_slow_estimate_01,
    // hs_dispatch_overhead_01, hs_lead_response_window_01,
    // hs_recurring_service_retention_01, hs_review_capture_01,
    // hs_truck_inventory_01, hs_paperwork_drowning_01,
    // hs_technician_recruiting_01, hs_pricing_visibility_01,
    // hs_compliance_tracking_01, hs_seasonal_capacity_swings_01.
    // Umbrella vertical covers plumbing, electrical, roofing, pool service,
    // pest control, painting, landscaping. Pain points are 90% shared.
    problems: [
      "Emergency calls (burst pipe, blown breaker, leaking roof) lost after-hours — go to whoever answers fastest",
      "Quote turnaround takes 2-3 days — customer accepts a competitor's same-day quote first",
      "Dispatching 6-15 techs by hand on a whiteboard eats 60-90 min every morning",
      "Web form leads sit unread for hours — customer books the shop that replies in 5 min",
      "Annual maintenance contracts expire silently — customer goes elsewhere next year",
      "Google review requests inconsistent — 4.6 rating undersells our actual quality",
      "Tech rolls up missing the right part — 40-min supply-house run kills the day",
      "End-of-week paperwork burns 6-10 hours per tech — invoices lag 2 weeks behind work",
      "Hiring a licensed plumber/electrician/roofer takes 3-6 months",
      "Per-job profitability is opaque — can't tell which job types or service areas pay",
      "Tech licenses, EPA, OSHA, insurance tracked in a stale spreadsheet",
      "Storm season triple-booked, quiet weeks idle — pure feast or famine",
      "Customer signature collection scattered between paper, phone, and email",
      "After-hours emergencies fall to owner's personal phone — not scalable",
      "Marketing dormant during slow weeks when capacity is open",
      "Cross-trade handoffs (plumbing → electrical → drywall) lose context",
      "Photo documentation for warranty claims missing or unsearchable",
      "Negative reviews unanswered for weeks — bleeding new lead trust",
    ],
    goals: [
      "AI receptionist that answers and qualifies emergency calls 24/7",
      "Same-day photo-to-quote AI so customers see a number within hours",
      "AI dispatcher that handles morning routing in 10 minutes instead of 90",
      "Instant SMS auto-reply to web form fills (under 5 minutes, every time)",
      "Auto-renewing maintenance contracts with prepay incentives — recurring revenue",
      "Auto-request Google reviews after every completed job",
      "Truck inventory tied to job calendar — right parts loaded automatically",
      "Voice-to-form for tech notes — paperwork done before tech leaves the driveway",
      "AI screening of trade applicants — only qualified candidates reach the owner",
      "Per-job profitability dashboard by service area, job type, and tech",
      "Compliance tracker with auto-reminders for license / cert / insurance renewals",
      "Demand forecasting + idle-week marketing campaigns to smooth seasonality",
    ],
    currentTools: [
      "ServiceTitan / Housecall Pro / Jobber / FieldEdge",
      "Workiz / BuildOps / ServiceFusion / mHelpDesk",
      "QuickBooks / Xero",
      "Google Business / Yelp / Angi / Thumbtack",
      "Podium / Birdeye / NiceJob (review acquisition)",
      "CompanyCam / Boost (jobsite photos)",
      "Mailchimp / Constant Contact (recall + drip)",
      "Square / Stripe (mobile payments)",
      "Calendly / Acuity (booking)",
      "DocuSign / Jotform (signatures + intake)",
      "FleetSharp / Verizon Connect / Samsara (fleet tracking)",
      "Slack / Teams (crew comms)",
      "RingCentral / OpenPhone / Dialpad (voice + SMS)",
      "Spreadsheets / Google Sheets (the catch-all)",
      "Zapier / Make / Pipedream (glue)",
      "ChatGPT / Claude / Gemini",
      "Indeed / ZipRecruiter (hiring)",
      "Paper notes + iPhone photos (the reality)",
    ],
  },
  accounting: {
    // PROBLEM_NODES (finance): late_invoice_payments_01, expense_management_01,
    // tax_prep_chaos_01, payroll_errors_01, fraud_detection_01.
    problems: [
      "Tax season is a 3-month nightmare — 60+ hour weeks",
      "Chasing clients for documents takes more time than the actual work",
      "Manual bank feed categorization eating hours every day",
      "Advisory services stuck in our head — no time to systematize them",
      "Client communication is scattered across email, text, and phone",
      "New client onboarding takes a week when it should take a day",
      "1099 generation at year-end is a frantic vendor data cleanup",
      "Sales tax filings vary by state — keeping up is a full-time job",
      "Payroll cross-state issues catch us by surprise (residency rules)",
      "Year-end close drags into February most years",
      "Software migration projects (Xero → QBO, etc.) eat weekends",
      "K-1 production for partnerships takes weeks",
      "Audit response asks pile up — pulling docs from email + drives",
      "Write-up accuracy varies by staff — review eats a manager",
      "Client deliverable QA is manual partner review only",
      "Peer review prep starts 60 days before the visit",
      "Capacity during tax season — overtime burns out junior staff",
      "Fee structure complexity — clients argue every quarter",
    ],
    goals: [
      "AI-powered bank categorization that learns each client's patterns",
      "Automated document collection — clients upload, AI organizes",
      "Onboard new clients in 24 hours with digital intake + auto-setup",
      "Package advisory services with AI-generated insights per client",
      "Single client portal for all communication and document sharing",
      "Reclaim 10+ hours/week of admin time for billable work",
      "Auto-flag payroll cross-state issues at month-end (not at audit)",
      "AI-generated K-1s + Schedule K-3s within hours of partnership close",
      "Continuous compliance monitoring (sales tax, payroll, 1099 prep)",
      "Audit-ready document trail organized per client per year",
      "Capacity dashboard so partners see overload before it burns staff",
      "Productized advisory packages with AI-generated client insights",
    ],
    currentTools: [
      "QuickBooks / Xero / Sage / FreshBooks",
      "Drake / Lacerte / UltraTax / ProConnect / TaxAct",
      "Gusto / ADP / Rippling / Paychex / Justworks",
      "Dext / Hubdoc / AutoEntry / Botkeeper",
      "Loom / Zoom",
      "Karbon / Jetpack Workflow / Canopy / TaxDome",
      "Bill.com / Melio / Ramp / Brex",
      "Avalara / TaxJar / Sovos (sales tax)",
      "Trolley / Track1099 (1099 prep)",
      "Mercury / Stripe / Plaid (banking integrations)",
      "Ignition / Practice Ignition (proposals)",
      "FreshBooks / Wave / Zoho Books",
      "ADP / Rippling (PEO)",
      "DocuSign / HelloSign / Adobe Sign",
      "Slack / Teams", "Calendly / Acuity",
      "Microsoft 365 / Google Workspace",
      "Zapier / Make",
    ],
  },
  ecommerce: {
    // PROBLEM_NODES (retail): cart_abandonment_01, inventory_guesswork_01,
    // checkout_friction_01, customer_churn_retail_01, low_aov_retail_01,
    // payment_failure_01, high_return_rate_01, pricing_pressure_01.
    problems: [
      "Cart abandonment over 70% — we're leaving money on the table",
      "Writing product descriptions and marketing emails takes all week",
      "Customer support tickets piling up — same questions over and over",
      "Ad spend going up but ROAS going down — no idea what's working",
      "Inventory management is reactive — we overstock or run out",
      "One-time buyers never come back (repeat purchase rate under 30%)",
      "Subscription churn is high — we lose 15%+ of subs per month",
      "Product image generation eats designer time per launch",
      "A/B testing is sporadic — no testing cadence",
      "Fraud chargebacks — manual review can't keep up with order volume",
      "Returns process is manual — RMA emails, refund delays, restocking labor",
      "International expansion stalled — currency, tax, shipping each a project",
      "COGS calculation is approximate — we don't know real per-unit margin",
      "Ad creative fatigue — performance drops every 2–3 weeks",
      "Influencer outreach + tracking lives in spreadsheets",
      "SEO content production for category pages is essentially zero",
      "Marketplace synchronization (Amazon, Etsy) drifts out of sync daily",
      "Post-purchase upsells/cross-sells leave revenue on the table",
    ],
    goals: [
      "Recover 15–25% of abandoned carts with AI-timed follow-ups",
      "AI-generated product copy and email campaigns that match our brand voice",
      "Chatbot handling 60%+ of support tickets — instant answers 24/7",
      "Clear attribution — know exactly which ads and channels drive profit",
      "Demand forecasting so we buy smart and never miss a sale",
      "Turn one-time buyers into loyal customers with personalized flows",
      "Reduce subscription churn 25–40% via cancel-flow saves + win-back",
      "AI-generated product imagery + lifestyle shots from a single brief",
      "Continuous A/B test pipeline driven by AI hypotheses",
      "Auto-generated SEO content for every category + collection",
      "Cross-marketplace inventory sync within minutes",
      "Smart post-purchase upsell flow lifting AOV 8–15%",
    ],
    currentTools: [
      "Shopify / WooCommerce / BigCommerce / Magento / Squarespace",
      "Klaviyo / Omnisend / Mailchimp / ActiveCampaign",
      "Gorgias / Zendesk / Intercom / Re:amaze (support)",
      "Google Analytics / GA4 / Mixpanel / Heap",
      "Meta Ads / Google Ads / TikTok Ads",
      "Zapier / Make / n8n",
      "ShipStation / Shippo / EasyShip",
      "Yotpo / Loox / Stamped / Junip (reviews + UGC)",
      "Recharge / Bold / Smartrr / Skio (subscriptions)",
      "Postscript / Attentive / Klaviyo SMS",
      "Smile.io / Yotpo Loyalty / LoyaltyLion",
      "Privy / Justuno / Klaviyo Forms (popups)",
      "Triple Whale / Northbeam / Polar (attribution)",
      "Returnly / Loop Returns / AfterShip Returns",
      "Order Desk / Skubana / Cin7 / TradeGecko",
      "Tapcart / Tapcart (mobile app builder)",
      "ChatGPT / Claude / Gemini",
      "Slack / Teams",
    ],
  },
  construction: {
    problems: [
      "Estimating takes too long — and we're still losing bids on price",
      "Change orders and scope creep eating our margins on every job",
      "Subcontractor coordination is chaos — texts, calls, voicemails",
      "Job costing is only accurate after the project is done (too late)",
      "Safety compliance paperwork piling up — one violation could shut us down",
      "Cash flow gaps between mobilization and final payment",
      "Bid pipeline tracking lives in spreadsheets — bids slip silently",
      "Subcontractor invoicing comes in at random — month-end scramble",
      "Lien waivers tracked manually (delayed payments to subs)",
      "OSHA documentation is incomplete or hard to retrieve at audit",
      "Equipment maintenance reactive — breakdowns at the worst moment",
      "Fuel + fleet costs untracked at the per-job level",
      "Customer change orders during build go undocumented half the time",
      "RFI (request for information) response time stretches schedules",
      "Punch lists printed and lost — closeout drags into following month",
      "Daily field reports + photos manual — no end-of-day rollup",
      "Warranty work tracking is ad hoc — repeat callbacks repeat",
      "Recruiting skilled labor (welders, carpenters, etc.) — months-long search",
    ],
    goals: [
      "AI-assisted estimating that gets bids out 2–3x faster",
      "Real-time job costing so we catch margin leaks while we can still fix them",
      "Digital change order tracking with automatic cost and schedule impact",
      "Centralized crew and sub communication — no more lost texts",
      "Automated safety checklists and compliance documentation",
      "Cash flow forecasting that accounts for project milestones and retainage",
      "Auto-generated daily field reports + photo logs from foreman phones",
      "Lien-waiver workflow tied to sub payment release",
      "Predictive equipment maintenance alerts before breakdown",
      "RFI response within 24 hours via AI-routed assignment",
      "Punch-list closeout tracked digitally — projects close on schedule",
      "AI-screened candidate pipeline for trades hiring",
    ],
    currentTools: [
      "Procore / Buildertrend / CoConstruct / JobNimbus",
      "QuickBooks / Sage / Foundation Software",
      "Sage 100 Contractor / Sage 300 CRE",
      "PlanGrid / Bluebeam / Autodesk Construction Cloud",
      "Excel / Spreadsheets / Google Sheets",
      "Google Drive / Dropbox / SharePoint",
      "Jobber / CompanyCam (jobsite docs + photos)",
      "Houzz Pro / Buildxact (estimating + bids)",
      "Trimble / Esticom / PlanSwift (takeoffs)",
      "ServiceTitan / Knowify (service + project mix)",
      "Bridgit Bench (workforce planning)",
      "Raken / SafetyCulture (daily reports + safety)",
      "Procore Pay / Levelset (lien waivers)",
      "DocuSign / HelloSign (contracts + change orders)",
      "RingCentral / OpenPhone (calls + SMS)",
      "Mailchimp / ActiveCampaign (homeowner CRM)",
      "Slack / Teams", "Zapier / Make",
    ],
  },
  credit_repair: {
    // PROBLEM_NODES: credit_repair_dispute_generation_01,
    // credit_repair_bureau_response_01, credit_repair_client_intake_01,
    // credit_repair_compliance_01, credit_repair_client_churn_01,
    // credit_repair_dispute_pipeline_01. Plus consumer-services patterns
    // (subscription billing, ID verification, e-sign workflows) shared with
    // debt-settlement and financial-counseling shops.
    problems: [
      "Drafting dispute letters by hand — 30-45 min per round per client",
      "Tracking 100s of active disputes across 3 bureaus on a spreadsheet",
      "30% of new leads drop off mid-intake (3 reports + CROA + ID + POA)",
      "CROA + state-by-state credit-services compliance is a constant fear",
      "90-day client churn at 30-50% — they get impatient before results land",
      "Marketing claims walk a thin line — one 'guaranteed' word risks investigation",
      "Bureau 30-day response clock slips on 15-20% of disputes",
      "Round-2 + round-3 strategy decided manually per client per bureau",
      "Recurring billing failures (payment retries, dunning) eat 5-10% of MRR",
      "Affiliate / referral source attribution lives in spreadsheets",
      "Client education during waiting periods is non-existent",
      "Cancel flow: one button, no save offer, no pause option",
      "Score-tracking + monthly progress reports manual or absent",
      "Objection handling on consultation calls is uneven across staff",
      "ID verification + e-signed CROA agreement workflow is manual",
      "FTC's 2-year record-retention requirement enforced via Google Drive folders",
      "Win-back outreach to past clients hasn't shipped in months",
      "Cross-state operations multiply bonding + filing requirements without a system",
    ],
    goals: [
      "AI dispute-letter generation with bureau-specific FCRA citations",
      "Pipeline dashboard tracking every active dispute with 30-day clock alerts",
      "Self-serve digital intake — credit pulls + CROA e-sign + ID verify in one flow",
      "Cut 90-day churn 30-50% with monthly progress reports + save offers",
      "Auto-generated client progress + score-trend dashboards",
      "Compliance tracker per state (CROA, bonding, filings, marketing review)",
      "Recurring-billing recovery flow (smart retry, dunning, pause options)",
      "Affiliate / referral attribution tied to client lifetime value",
      "Education drip during the 30-day waiting period (keeps them engaged)",
      "AI-drafted consultation call scripts + objection handlers",
      "Predictive model flagging clients likely to cancel in next 30 days",
      "Win-back automation for cancelled clients at 60 / 90 / 180 day marks",
    ],
    currentTools: [
      "Credit Repair Cloud / Disputefox / TurboDispute",
      "ScoreCEO / Client Dispute Manager",
      "DocuSign / HelloSign / SignNow",
      "Stripe / Authorize.net / NMI (recurring billing)",
      "QuickBooks / Xero",
      "SmartCredit / IdentityIQ / MyFreeScoreNow (credit pulls)",
      "Twilio / RingCentral (calls + SMS)",
      "ClickFunnels / Leadpages (marketing)",
      "ActiveCampaign / Mailchimp",
      "Calendly / Acuity",
      "Zapier / Make",
      "Slack / Teams",
    ],
  },
  other: {
    // PROBLEM_NODES (other): cash_flow_uncertainty_01, staff_turnover_high_01,
    // customer_support_slow_01, social_media_inconsistent_01,
    // employee_training_01, reporting_manual_01, reputation_management_01,
    // content_creation_backlog_01, competitor_visibility_01.
    problems: [
      "Spending hours on repetitive tasks that feel like they should be automated",
      "Hard to keep up with customer inquiries — response time is too slow",
      "Bookkeeping and invoicing taking time away from actual revenue work",
      "Marketing feels like guesswork — posting content with no idea if it works",
      "Hiring and training new people is slow and expensive",
      "No clear picture of what's working and what's not in the business",
      "Cash flow is unpredictable — surprise dips every month",
      "Staff turnover is high — we keep losing people we just trained",
      "Customer support response times stretching past 24 hours",
      "Social media posting is inconsistent — weeks of silence then a burst",
      "Employee training relies on shadowing veterans (slow + uneven)",
      "Monthly reporting eats 1-2 days of someone's time",
      "Online reputation drifts — reviews go unmonitored",
      "Content creation backlog — half-written posts pile up",
      "Competitive intelligence is essentially zero — we miss what rivals ship",
      "Email overload — important client messages get lost",
      "No SOPs documented — every task takes 'whoever knows it'",
      "Lost passwords + access requests waste owner time weekly",
    ],
    goals: [
      "Automate the repetitive stuff — save 10+ hours/week",
      "Respond to customers instantly, even outside business hours",
      "Hands-off invoicing and expense tracking",
      "Marketing that runs itself and actually brings in leads",
      "Faster onboarding — new hires productive in days, not months",
      "A simple dashboard that shows what's working and what needs attention",
      "Predictable cash flow with weekly forecast vs actual",
      "Reduce staff turnover with structured onboarding + clear paths",
      "AI-monitored reviews + reputation across all platforms",
      "Always-on social media calendar with AI-drafted posts",
      "Documented SOPs covering 80% of recurring tasks",
      "Auto-generated weekly business dashboard delivered to your inbox",
    ],
    currentTools: [
      "QuickBooks / Xero / FreshBooks / Wave",
      "Google Workspace / Microsoft 365",
      "Slack / Teams / Discord",
      "Zapier / Make / n8n",
      "Notion / Airtable / Coda / ClickUp",
      "Salesforce / HubSpot / Pipedrive / Attio",
      "Asana / Trello / Monday / Linear",
      "Stripe / Square / PayPal / Mercury (payments)",
      "Calendly / Acuity / SavvyCal",
      "Loom / Zoom / Riverside (recording)",
      "Mailchimp / ActiveCampaign / Brevo / Customer.io",
      "DocuSign / HelloSign / PandaDoc",
      "Canva / Figma / Adobe Express",
      "Google Analytics / GA4 / Plausible",
      "Hotjar / FullStory (session replay)",
      "ChatGPT / Claude / Gemini",
      "Loom / Vidyard (async video)",
      "Webflow / WordPress / Squarespace (site builder)",
    ],
  },
};

// businessType overrides — shown first when set, merged with industry suggestions
const BUSINESS_TYPE_SUGGESTIONS = {
  physical: {
    problems: [
      "No way to track foot traffic or in-store conversion rate",
      "Inventory managed with spreadsheets — stockouts happen",
      "Customer loyalty program is manual or nonexistent",
      "Staff scheduling takes 3–5 hours every week",
      "Missing out on repeat visits — no automated follow-up",
    ],
    goals: [
      "Track in-store traffic and understand peak hours with AI",
      "Automate inventory reordering to eliminate stockouts",
      "Launch a digital loyalty program that drives repeat visits",
      "Cut scheduling time in half with AI-assisted shift planning",
      "Automate review requests to grow Google/Yelp ratings",
    ],
    currentTools: [
      "Square / Clover POS", "QuickBooks / Xero", "Lightspeed Retail",
      "Google Business Profile", "Mailchimp", "Spreadsheets",
    ],
  },
  ecommerce: {
    problems: [
      "High cart abandonment — few checkouts actually recovered",
      "Lots of site traffic but low add‑to‑cart rate",
      "Spending hours each week on product descriptions and emails",
      "No clear view of which campaigns or channels drive sales",
      "Post‑purchase experience is manual and inconsistent",
    ],
    goals: [
      "Recover more abandoned carts automatically (AI recovers 18–27%)",
      "Increase AOV with smarter cross‑sells and bundles",
      "Automate product copy and email campaigns without losing brand voice",
      "Get a clear weekly view of which channels really drive revenue",
      "Turn one‑time buyers into repeat customers with automated flows",
    ],
    currentTools: [
      "Shopify / WooCommerce", "Klaviyo / Omnisend", "Google Analytics",
      "Zapier / Make", "Gorgias / Zendesk", "Triple Whale / Northbeam",
    ],
  },
  services: {
    problems: [
      "Too many manual proposals and follow‑ups eating owner time",
      "Leads slipping through the cracks in the pipeline",
      "Calendar and scheduling chaos across the team",
      "Owner doing most client communication and delivery personally",
      "No consistent onboarding — every client experience is different",
    ],
    goals: [
      "Cut proposal and SOW writing time in half",
      "Automate lead qualification and intake to focus on best‑fit clients",
      "Reduce time spent on scheduling and follow‑ups",
      "Standardize delivery with templates and checklists",
      "Build a referral engine that works without manual outreach",
    ],
    currentTools: [
      "Calendly / Acuity", "HubSpot / Pipedrive", "Notion / ClickUp",
      "DocuSign / PandaDoc", "Zoom / Teams", "QuickBooks / Xero",
    ],
  },
  both: {
    problems: [
      "Product and service revenue tracked in separate systems",
      "No unified funnel from content → product → service offers",
      "Manual handoff between store orders and service work",
      "Customers not automatically moved from buyer to client",
    ],
    goals: [
      "Single pipeline for store buyers and service clients",
      "Turn product buyers into long‑term service clients automatically",
      "One AI‑assisted dashboard to track revenue across both sides",
      "Unified email flows that work for products and services",
    ],
    currentTools: [
      "HubSpot (CRM + ecommerce)", "Zapier / Make", "Shopify + Calendly",
      "Klaviyo", "QuickBooks / Xero",
    ],
  },
};

// R36 (Sarah R36 BRUTAL walk): commas inside chip labels broke the
// comma-joined storage contract. A buyer who clicked 4 chips where 3
// contained commas saw the live counter say "10 problems selected" —
// the split-by-comma parser inflated each comma-containing chip into
// multiple "selected" tokens. Worse, `selected.includes(label)` could
// never match a re-split label, so clicking those chips a second time
// never deselected them — it appended a duplicate copy. Strip commas
// at the suggestion source so every downstream consumer sees a
// comma-free label. " — " replacement preserves the readable cadence
// the original copy intended. Empty / null inputs pass through.
const _stripChipCommas = (s) => {
  if (typeof s !== "string" || !s.includes(",")) return s;
  return s
    .replace(/(\d),(\d)/g, "$1$2")  // preserve numeric formatting: $1,500 → $1500
    .replace(/, /g, " — ")          // sentence commas: "texts, voicemail" → "texts — voicemail"
    .replace(/,/g, " ");            // any remaining commas
};

const getSuggestions = (industry, businessType, subVertical = "") => {
  const btData = BUSINESS_TYPE_SUGGESTIONS[businessType];
  // R35 (Sarah verdict, P0): sub-vertical override. When the buyer
  // arrived via /pm or /property-management, subVertical is set to
  // "property_management" and the chip pool resolves to the dedicated
  // PM entry below — NOT the brokerage-leaning real_estate pool. Same
  // pattern will cover future sub-verticals (dental specialty, primary
  // care vs urgent care, etc.) by passing subVertical through.
  const lookupKey = (
    subVertical && INDUSTRY_SUGGESTIONS[subVertical]
      ? subVertical
      : industry
  );
  const indData = INDUSTRY_SUGGESTIONS[lookupKey] || INDUSTRY_SUGGESTIONS.other;

  // R39 (Sarah R36d verdict): when a sub-vertical chip pool is in use,
  // skip the businessType merge entirely. The PM pool is curated as a
  // complete 18-chip set; merging 5 generic 'services' chips (calendar
  // chaos / manual proposals / etc.) on top inflates the visible
  // pool to 23 and leaks brokerage-funnel language into a PM walk.
  // Sarah called this cosmetic but it's a one-line fix.
  const _useSubVerticalPool = subVertical && INDUSTRY_SUGGESTIONS[subVertical];

  if (btData && !_useSubVerticalPool) {
    // Industry-specific leads, business-type pads. Previously btArr led, which
    // buried industry-native pain (e.g. a real estate agent picked
    // business_type=services and got 5 generic "calendar chaos / manual
    // proposals" cards before any RE-specific ones).
    // Earlier: capped each list at 8. Now we surface the FULL union so the
    // ProblemCards/SuggestionChips can default-render 6 + offer "Show more"
    // for the rest. Cap at 24 just to bound DOM size on truly long lists.
    const merge = (indArr, btArr) => {
      const seen = new Set(indArr);
      const extra = btArr.filter((s) => !seen.has(s));
      return [...indArr, ...extra].slice(0, 24).map(_stripChipCommas);
    };
    return {
      problems: merge(indData.problems, btData.problems),
      goals: merge(indData.goals, btData.goals),
      currentTools: merge(indData.currentTools, btData.currentTools),
    };
  }

  return {
    problems: (indData.problems || []).slice(0, 24).map(_stripChipCommas),
    goals: (indData.goals || []).slice(0, 24).map(_stripChipCommas),
    currentTools: (indData.currentTools || []).slice(0, 24).map(_stripChipCommas),
  };
};

// ---------- Chip label → backend problem ID matcher ----------
// Marketing-curated chip labels (e.g. "High cart abandonment (70%+ costs $250-400 daily)")
// don't always equal the backend's canonical problem_text ("People add items to
// cart but don't complete checkout..."), so we score by token-overlap (Jaccard)
// to find the most likely backend ID for each selected label. Pure function —
// kept at module scope so React doesn't recreate it on every render.
const _STOP_WORDS = new Set([
  "the","a","an","and","or","but","is","are","was","were","be","been","being",
  "of","in","on","at","to","for","with","by","from","as","this","that","these",
  "those","i","my","we","our","you","your","they","their","it","its","up","out",
  "about","into","like","can","will","do","does","did","not","no","too","very",
  "than","then","so","just","only","also","over","under","more","less","most",
  "least","high","low","new","old","big","small","good","bad","other","much",
  "many","few","get","got","getting","make","made","making","keep","kept",
  "have","has","had","need","want","cant","cannot","wont","dont","doesnt",
  "didnt","its","your","yours","ours","mine","theirs",
]);
const _tokenize = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")  // strip punctuation
    .split(/\s+/)
    // Drop pure-digit tokens — chip labels include statistics ("70%+", "$250")
    // that bloat the token count without carrying semantic meaning. Also drop
    // tokens shorter than 3 chars and stopwords.
    .filter((w) => w.length >= 3 && !_STOP_WORDS.has(w) && !/^\d+$/.test(w));

const _scoreOverlap = (labelTokens, corpusTokens) => {
  if (!labelTokens.length || !corpusTokens.length) return { score: 0, hits: 0 };
  const corpusSet = new Set(corpusTokens);
  let hits = 0;
  for (const t of labelTokens) if (corpusSet.has(t)) hits += 1;
  // Coverage: what fraction of the chip's substantive words showed up in the
  // node's corpus (id + family + manifestations + problem_text). Using
  // label-side as the denominator means a short chip matched against a long
  // problem_text isn't penalized for the asymmetry. We also return absolute
  // `hits` so the caller can require >= 2 — this guards against a 2-token
  // chip + 1 incidental match clearing the threshold on coverage alone.
  return { score: hits / labelTokens.length, hits };
};

// R43.1 (Sarah R36i verdict): sub-vertical-aware problem node selection.
// Two nodes can share the same problem_family_id (e.g. re_vendor_coordination_01
// + pm_vendor_coordination_01 both family="vendor_coordination"). Without
// preference, catalog file order decides the tie — and pm_* was added after
// re_* in the file, so PM walks bound their vendor-coord chip to the
// brokerage node ("Photographer, inspector, stager, repair vendors...").
//
// R45 (Maya verdict — generalize beyond PM): originally this checked only
// a hard-coded `pm_` prefix when sub_vertical=property_management. Two
// problems with that: (1) when we add dental specialties, healthcare sub-
// types, or e-commerce sub-types, each needs a new prefix entry — easy to
// forget; (2) it doesn't read the node's actual `sub_vertical` field which
// is the canonical source of truth. Updated to prefer nodes whose
// `sub_vertical` field matches the buyer's first, then fall through to the
// id-prefix heuristic for backwards compat with nodes added before the
// `sub_vertical` field was standard (pm_*, re_*).
const _SUB_VERTICAL_ID_PREFIXES = {
  property_management: "pm_",
};
const _reorderCatalogForSubVertical = (catalog, subVertical) => {
  const sv = (subVertical || "").toLowerCase().trim();
  if (!sv) return catalog;
  const prefix = _SUB_VERTICAL_ID_PREFIXES[sv];
  const preferred = [];
  const rest = [];
  for (const p of catalog) {
    const nodeSv = (p.sub_vertical || "").toLowerCase().trim();
    // Pass-1: node explicitly tags its sub_vertical and matches buyer.
    if (nodeSv && nodeSv === sv) {
      preferred.push(p);
      continue;
    }
    // Pass-2: legacy id-prefix heuristic for nodes that pre-date the
    // sub_vertical field convention (pm_vendor_coordination_01 etc.).
    if (prefix && (p.id || "").toLowerCase().startsWith(prefix)) {
      preferred.push(p);
      continue;
    }
    rest.push(p);
  }
  return [...preferred, ...rest];
};

const mapLabelsToProblemIds = (labels, catalog, subVertical = "") => {
  if (!Array.isArray(labels) || labels.length === 0) return [];
  if (!Array.isArray(catalog) || catalog.length === 0) return [];
  // R43.1: sub-vertical preference — when buyer is PM, pm_* nodes get
  // first crack at the phrase-match + Jaccard passes below.
  catalog = _reorderCatalogForSubVertical(catalog, subVertical);
  // Build matching context per node:
  //   - phrase: the problem_family_id rendered as a hyphen+space variant
  //     (e.g. "cart_abandonment" -> matches "cart abandonment" OR
  //     "cart-abandonment"). This is the highest-signal match: if a chip
  //     literally mentions the family by name, the binding is unambiguous.
  //   - tokens: the union of id + family + category + problem_text +
  //     typical_manifestations, used as a fallback when phrase-match misses.
  const catalogCtx = catalog.map((p) => {
    const familyRaw = (p.problem_family_id || "").toLowerCase();
    const familyPhrase = familyRaw.replace(/_/g, " ").trim();
    const familyDashed = familyRaw.replace(/_/g, "-").trim();
    const corpus = [
      (p.id || "").replace(/_/g, " ").replace(/\d+/g, " "),
      familyPhrase,
      (p.category || "").replace(/_/g, " "),
      p.problem_text || "",
      Array.isArray(p.typical_manifestations) ? p.typical_manifestations.join(" ") : "",
    ].join(" ");
    return {
      id: p.id,
      familyPhrase,
      familyDashed,
      tokens: _tokenize(corpus),
    };
  });
  const ids = [];
  const used = new Set(); // 1 chip → 1 ID; don't double-bind labels to the same node
  for (const label of labels) {
    const lower = (label || "").toLowerCase();
    const labelTokens = _tokenize(label);
    if (!labelTokens.length) continue;

    // Pass 1 — phrase override. If the chip literally contains the family
    // name (e.g. chip="High cart abandonment (70%+ ...)" contains
    // "cart abandonment"), bind directly. This is the same way a human would
    // route the chip — read the keyword, pick the bucket. Strongest signal.
    let phraseMatch = null;
    for (const c of catalogCtx) {
      if (used.has(c.id)) continue;
      if (!c.familyPhrase || c.familyPhrase.length < 4) continue;
      if (lower.includes(c.familyPhrase) || lower.includes(c.familyDashed)) {
        phraseMatch = c.id;
        break;
      }
    }
    if (phraseMatch) {
      ids.push(phraseMatch);
      used.add(phraseMatch);
      continue;
    }

    // Pass 2 — token-overlap fallback. Used when phrase-match misses (chip
    // wording doesn't echo the family name verbatim).
    let best = null;
    let bestScore = 0;
    let bestHits = 0;
    for (const c of catalogCtx) {
      if (used.has(c.id)) continue;
      const { score, hits } = _scoreOverlap(labelTokens, c.tokens);
      if (score > bestScore) { bestScore = score; bestHits = hits; best = c.id; }
    }
    // Round-12 (Sarah R11): the previous threshold (score >= 0.4 AND
    // hits >= 2) silently dropped chips whose marketing wording shares
    // only 1-2 strong tokens with a catalog node — Sarah tapped 3 chips
    // and only 1 reached the backend. Loosened to score >= 0.3 OR
    // hits >= 2 (any reasonable signal binds), so 2 chips don't get
    // dropped to backend free-text matching where the round-11
    // chip-only mode would then fall through entirely.
    if (best && (bestScore >= 0.3 || bestHits >= 2)) {
      ids.push(best);
      used.add(best);
    } else if (typeof console !== "undefined" && console?.info) {
      // Diagnostic — never throws, only fires in dev console.
      // Round-14 (Sarah R13): drop the slice(0, 60) cap. Reviewer spent
      // 3 rounds suspecting a duplicate-word truncation bug because the
      // log read "...over and" — the actual label was "...over and
      // over" but the cap chopped at char 60. Logging the FULL label
      // makes any future truncation analysis unambiguous.
      console.info(
        `[chip-resolver] no ID for label ${JSON.stringify(label || "")} ` +
        `(best=${best || "none"}, score=${bestScore.toFixed(2)}, hits=${bestHits})`
      );
    }
  }
  return ids;
};

// ---------- Safe localStorage loader ----------
// DELIBERATELY excludes `problems`, `goals`, `currentTools` — those are bound
// to the current-session industry and must NOT leak across sessions. External
// reviewer verified the failure mode: clear localStorage, pick Ecommerce fresh,
// submit — payload still contained a mix of Real Estate problems + goals from a
// prior session because those three fields were restored from the stale
// localStorage entry. The rule engine downstream either crashes or rejects a
// submission that carries problem labels that don't exist in the submitted
// industry's taxonomy, which is the root cause of "Report generation failed".
const VALID_FORM_KEYS = [
  "businessName",
  "industry",
  "businessSize",
  "budget",
  "businessType",
  "ecommercePlatform",
  "aiAssistant",
  "email",
  "hoursWasted",
  "revenueImpact",
  "monthlyRevenue",
  "dataReadiness",
  "aiConcerns",
  "teamLiteracy",
  "trajectory",
];

const DEFAULT_FORM_DATA = {
  businessName: "",
  industry: "",
  businessSize: "",
  budget: "",
  businessType: "",
  ecommercePlatform: "",
  aiAssistant: "",
  problems: "",
  goals: "",
  currentTools: "",
  email: "",
  hoursWasted: "",
  revenueImpact: "",
  monthlyRevenue: "",
  dataReadiness: "",
  aiConcerns: "",
  teamLiteracy: "",
  trajectory: "",
};

const loadSavedFormData = () => {
  try {
    const saved = localStorage.getItem("auditFormData");
    if (!saved) return DEFAULT_FORM_DATA;
    const parsed = JSON.parse(saved);
    if (typeof parsed !== "object" || parsed === null) return DEFAULT_FORM_DATA;
    const sanitized = { ...DEFAULT_FORM_DATA };
    for (const key of VALID_FORM_KEYS) {
      if (typeof parsed[key] === "string") {
        sanitized[key] = parsed[key];
      }
    }
    return sanitized;
  } catch (e) {
    console.error("Failed to parse saved form data", e);
    localStorage.removeItem("auditFormData");
    return DEFAULT_FORM_DATA;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// Exceptional UI primitives for the audit form.
// Research: button/segmented inputs convert 15-25% higher than text, 10-15%
// higher than radio groups on touch devices. Icon-scaled severity indicators
// (not emojis) keep the B2B feel intact while reducing cognitive load.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * SegmentedPicker — tappable button row with optional icon scaling per option.
 * Replaces <Select> dropdowns for 2-6 discrete choices.
 *
 * options: [{ value, label, sublabel?, icon?, iconCount?, iconColor? }]
 *   iconCount: 1-4 — duplicates the icon to visually escalate severity.
 *
 * Use cases: businessSize, budget, hoursWasted ($-sign scale), revenueImpact.
 */
const SegmentedPicker = ({ value, onChange, options, columns = null, ariaLabel }) => {
  // Column classes pinned to the number of options, not arbitrarily capped at 5.
  // Prior version used `sm:grid-cols-4 md:grid-cols-5` which truncated labels
  // whenever the picker sat inside a half-width parent (step 2's old
  // md:grid-cols-2 container). Since step 2 now stacks the pickers vertically
  // they always render at full container width, so we can just match columns
  // to the option count and let the labels breathe.
  const n = options.length;
  const colClass =
    columns === 2  ? "grid-cols-2" :
    columns === 3  ? "grid-cols-2 sm:grid-cols-3" :
    columns === 4  ? "grid-cols-2 sm:grid-cols-4" :
    n === 5        ? "grid-cols-2 md:grid-cols-5" :  // skip sm — 5-across at 640px viewport is too tight for "$10k–$25k · Scale"
    n === 4        ? "grid-cols-2 sm:grid-cols-4" :
    n === 3        ? "grid-cols-3" :
    n <= 2         ? "grid-cols-2" :
                     "grid-cols-2 sm:grid-cols-3 md:grid-cols-5";
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={`grid ${colClass} gap-2.5`}>
      {options.map((opt) => {
        const active = value === opt.value;
        const Icon = opt.icon;
        const iconCount = opt.iconCount || (Icon ? 1 : 0);
        const iconColor = opt.iconColor || (active ? "text-white" : "text-slate-500 group-hover:text-[#3b82f6]");
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            title={opt.sublabel ? `${opt.label} — ${opt.sublabel}` : opt.label}
            className={`group relative overflow-hidden rounded-xl border px-4 py-4 text-left transition-all duration-200 min-w-0 ${
              active
                ? "bg-gradient-to-br from-blue-500/20 via-blue-500/10 to-blue-500/5 border-blue-500/60 shadow-lg shadow-blue-500/20 scale-[1.02]"
                : "bg-slate-950/50 border-white/10 hover:border-blue-500/40 hover:bg-slate-900/70 active:scale-[0.98]"
            }`}
          >
            {/* Active glow */}
            {active && (
              <motion.div
                layoutId="segmented-glow"
                className="absolute inset-0 bg-gradient-to-br from-[#3b82f6]/10 to-transparent pointer-events-none"
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              />
            )}
            {/* Icon scale — 1-5 icons depending on tier/severity */}
            {Icon && iconCount > 0 && (
              <div className="flex items-center gap-0.5 mb-2 relative">
                {Array.from({ length: iconCount }).map((_, i) => (
                  <Icon key={i} className={`w-4 h-4 flex-shrink-0 ${active ? "text-[#3b82f6]" : iconColor} transition-colors`} strokeWidth={active ? 2.5 : 2} />
                ))}
              </div>
            )}
            <div className={`text-[15px] font-semibold leading-tight relative pr-5 ${active ? "text-white" : "text-slate-200"}`}>
              {opt.label}
            </div>
            {opt.sublabel && (
              <div className={`text-xs mt-1 relative ${active ? "text-blue-300" : "text-slate-500"}`}>
                {opt.sublabel}
              </div>
            )}
            {active && (
              <div className="absolute top-3 right-3 w-4 h-4 rounded-full bg-[#3b82f6] flex items-center justify-center flex-shrink-0">
                <Check className="w-2.5 h-2.5 text-slate-950" strokeWidth={3} />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

/**
 * IndustryCardGrid — 6 popular industries shown as cards; remaining 12 hidden
 * behind a "Show all industries" expander with search. Replaces the 18-option
 * dropdown on Step 1.
 */
const IndustryCardGrid = ({ value, onChange, t, ariaLabel }) => {
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState("");

  // Top 6 chosen by demand research (Legal/Accounting/Dental) + primary verticals.
  // Ecommerce gets the "Popular" badge since Shopify traffic is strongest.
  // `aliases` powers search — common business types that map to each industry
  // but wouldn't show in the user's scan of sublabels.
  const TOP_6 = [
    { value: "ecommerce",        icon: "🛒", label: "E-commerce",         sublabel: "Shopify, WooCommerce, DTC brands", popular: true,
      aliases: ["shopify","woocommerce","online store","dtc","direct to consumer","dropshipping","amazon seller","etsy"] },
    { value: "dental",           icon: "🦷", label: "Dental",             sublabel: "Practices, orthodontics, pediatric",
      aliases: ["dentist","orthodontist","ortho","pediatric dental","oral surgery","periodontist","endodontist"] },
    { value: "legal",            icon: "⚖️", label: "Legal",              sublabel: "Solo + small firms, attorneys",
      aliases: ["law firm","attorney","lawyer","paralegal","litigation","estate planning","family law","immigration"] },
    { value: "accounting",       icon: "📊", label: "Accounting",         sublabel: "Bookkeeping, tax prep, CPA",
      aliases: ["cpa","tax preparer","bookkeeper","enrolled agent","payroll service","quickbooks proadvisor"] },
    { value: "hvac",             icon: "❄️", label: "HVAC", sublabel: "Heating, cooling, ductwork, water heaters",
      aliases: ["heating","cooling","ac repair","furnace","ductwork","water heater","mini split","ventilation","refrigeration"] },
    { value: "home_services",    icon: "🛠️", label: "Home Services / Trades", sublabel: "Plumbing, electrical, roofing, pool, pest, painting, landscaping",
      aliases: ["plumbing","plumber","electrician","electrical","roofing","roofer","pool service","pool cleaning","pest control","pest","painting","painter","landscaping","handyman","field service","trade services"] },
    { value: "med_spa",          icon: "💆", label: "Med Spa / Salon",    sublabel: "Beauty, wellness, esthetician",
      aliases: ["salon","spa","hair salon","nail salon","massage","barbershop","tattoo","lashes","botox","injectables"] },
  ];
  const REST = [
    { value: "restaurant",           icon: "🍽️", label: "Restaurant",           sublabel: "Cafes, bars, catering, food trucks",
      aliases: ["cafe","bar","brewery","bakery","pizzeria","food truck","caterer","coffee shop"] },
    { value: "hospitality",          icon: "🏨", label: "Hospitality",          sublabel: "Hotels, airbnb, event venues",
      aliases: ["hotel","motel","airbnb","bed and breakfast","vrbo","event venue","wedding venue"] },
    { value: "retail",               icon: "🛍️", label: "Retail",               sublabel: "Brick-and-mortar stores",
      aliases: ["store","boutique","gift shop","thrift","consignment","pop-up","kiosk"] },
    { value: "healthcare",           icon: "🏥", label: "Healthcare",           sublabel: "Clinics, chiropractic, therapy, vet",
      aliases: ["clinic","chiropractor","physical therapy","therapist","psychologist","veterinarian","vet","optometry","audiology"] },
    { value: "real_estate",          icon: "🏠", label: "Real Estate",          sublabel: "Agents, brokerages, teams",
      aliases: ["realtor","broker","title company","mortgage"] },
    // R37 (Sarah R36c verdict): Property Management is a distinct industry
    // path from brokerage Real Estate — operators dispatch maintenance,
    // generate owner statements, and screen tenants under FCRA. Picking
    // this internally sets industry=real_estate (for engine compatibility
    // with existing real_estate tools + nodes) + subVertical=property_management
    // (which routes the PM chip pool, PM problem_nodes, PM tool blocklist,
    // and PM-aware copy throughout the flow). Without this dedicated
    // option, a PM operator who arrived through /audit directly had no
    // way to flag themselves as PM and got dropped into the brokerage
    // funnel — Sarah's R36c catastrophic FAIL.
    { value: "property_management",  icon: "🏘️", label: "Property Management", sublabel: "PM operators, owner-managers, leasing teams",
      aliases: ["property manager","landlord","pm","rental management","leasing","apartment management"] },
    { value: "auto_repair",          icon: "🔩", label: "Auto Repair",          sublabel: "Mechanics, body shops, detailing",
      aliases: ["mechanic","body shop","transmission","collision","detailing","tire shop","oil change","muffler"] },
    { value: "construction",         icon: "🏗️", label: "Construction",         sublabel: "General contractors, builders",
      aliases: ["contractor","general contractor","builder","remodeling","renovation","concrete","framing"] },
    { value: "technology",           icon: "💻", label: "Technology / SaaS",    sublabel: "Software, IT services, dev shops",
      aliases: ["saas","software","it services","web design","app developer","hosting","managed it","msp","cybersecurity"] },
    { value: "professional_services", icon: "💼", label: "Professional Services", sublabel: "Consulting, agencies, call centers, BPO",
      aliases: ["consulting","consultant","agency","marketing agency","pr","coaching","call center","bpo","virtual assistant","freelancer","contractor services"] },
    { value: "finance",              icon: "💰", label: "Finance",              sublabel: "Advisory, insurance, lending, fintech",
      aliases: ["financial advisor","insurance agent","lending","loan officer","mortgage broker","investment","wealth management","fintech"] },
    { value: "credit_repair",        icon: "📊", label: "Credit Repair",        sublabel: "Credit repair, debt settlement, financial counseling",
      aliases: ["credit repair","debt settlement","debt relief","credit counseling","financial counseling","credit consulting","credit services"] },
    { value: "manufacturing",        icon: "🏭", label: "Manufacturing",        sublabel: "Producers, suppliers, fabricators",
      aliases: ["factory","producer","supplier","fabricator","3d printing","machine shop","distributor"] },
    { value: "education",            icon: "📚", label: "Education",            sublabel: "Schools, tutoring, training, coaching",
      aliases: ["school","tutor","tutoring","online course","training","coach","academy","preschool","childcare","daycare"] },
    { value: "other",                icon: "✨", label: "Something else",      sublabel: "Tell us below — we'll tailor it",
      aliases: ["nonprofit","church","fitness","gym","yoga","photography","wedding","freight","logistics"] },
  ];

  const ALL = [...TOP_6, ...REST];
  const q = search.trim().toLowerCase();
  const filtered = q
    ? ALL.filter((o) =>
        o.label.toLowerCase().includes(q) ||
        o.sublabel.toLowerCase().includes(q) ||
        (o.aliases || []).some((a) => a.includes(q))
      )
    : null;

  const selectedCard = value ? ALL.find((o) => o.value === value) : null;
  const showingList = q ? filtered : (expanded ? [...TOP_6, ...REST] : TOP_6);
  const showSelectedOutsideTop = selectedCard && !q && !expanded && !TOP_6.some((o) => o.value === value);

  const Card = ({ opt }) => {
    const active = value === opt.value;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={active}
        onClick={() => onChange(opt.value)}
        title={`${opt.label} — ${opt.sublabel}`}
        className={`group relative overflow-hidden rounded-2xl border px-3 sm:px-4 py-4 pr-8 text-left transition-all duration-200 min-w-0 ${
          active
            ? "bg-gradient-to-br from-blue-500/20 via-blue-500/10 to-purple-500/10 border-blue-500/60 shadow-lg shadow-blue-500/25 scale-[1.03]"
            : "bg-slate-950/50 border-white/10 hover:border-blue-500/40 hover:bg-slate-900/70 hover:-translate-y-0.5 active:scale-[0.98]"
        }`}
      >
        {opt.popular && !active && (
          <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider bg-blue-500/20 text-[#3b82f6] border border-blue-500/30 px-1.5 py-0.5 rounded-full">
            Popular
          </span>
        )}
        {active && (
          <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#3b82f6] flex items-center justify-center shadow-md flex-shrink-0">
            <Check className="w-3 h-3 text-slate-950" strokeWidth={3} />
          </div>
        )}
        <div className="text-3xl mb-2 leading-none">{opt.icon}</div>
        <div className={`text-sm font-bold leading-tight break-words ${active ? "text-white" : "text-slate-200"}`}>
          {opt.label}
        </div>
        <div className={`text-[11px] mt-1 leading-snug break-words line-clamp-2 ${active ? "text-blue-300" : "text-slate-500"}`}>
          {opt.sublabel}
        </div>
      </button>
    );
  };

  return (
    <div role="radiogroup" aria-label={ariaLabel} className="space-y-3">
      {/* Show the user's selected option prominently if it's in REST and we're collapsed */}
      {showSelectedOutsideTop && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Card opt={selectedCard} />
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {(showingList || []).slice(0, expanded || q ? 50 : 6).map((opt) => (
          <Card key={opt.value} opt={opt} />
        ))}
      </div>

      {q && filtered && filtered.length === 0 && (
        <p className="text-center text-sm text-slate-500 py-6">
          No match for "{search}". Pick "Something else" below — we'll customize from there.
        </p>
      )}

      {!q && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full text-center text-sm text-[#3b82f6] hover:text-blue-300 font-semibold py-2.5 border border-dashed border-blue-500/30 rounded-xl hover:border-blue-500/60 hover:bg-blue-500/5 transition-all"
        >
          Show all {TOP_6.length + REST.length} industries →
        </button>
      )}

      {(expanded || q) && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search industries..."
            className="w-full bg-slate-950/50 border border-white/10 text-white placeholder:text-slate-500 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-blue-500/60"
          />
        </div>
      )}
    </div>
  );
};

/**
 * ProblemCards — tappable cards ARE the primary input. Textarea becomes optional.
 * Each card represents a real industry pain point. Multi-select into the
 * existing formData.problems field as a comma-joined string (unchanged contract).
 *
 * R31.4: 5-at-a-time window + circular refresh button. The 18-chip catalog
 * with "Show more" expansion read as a long list of tax-form checkboxes —
 * even cleaned up with icons + animation, 18 simultaneous decisions ahead
 * of any visible payoff was the dominant Step-3 bail. The refresh button
 * cycles through the catalog 5 at a time so the cognitive load is "scan
 * 5, decide, refresh if none fit" instead of "scan 18, prioritize, second-
 * guess." Selected chips stay visible across cycles regardless of which
 * window they came from.
 */
const ProblemCards = ({ industry, value, onChange, suggestions, t, subVertical = "" }) => {
  // Parse current value into an array
  // R44 (Maya verdict): "3 clicks → 2 counted" was a stale-closure race.
  // The toggle handler captured `selected` parsed from the `value` prop
  // at render time. When the user clicked faster than React committed
  // the parent setFormData → new prop, the second click read the stale
  // pre-first-click `selected` and stomped the first click's append.
  // Fix: use functional state via a ref so the toggle handler always
  // reads the LATEST committed value, even before React's next render.
  // valueRef updates synchronously on commit; toggle re-parses fresh.
  const valueRef = React.useRef(value);
  React.useEffect(() => { valueRef.current = value; }, [value]);

  const selected = (value || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const toggle = (label) => {
    // Read the latest value at click time, not the captured prop. If
    // a prior rapid click hasn't propagated to `value` yet, valueRef
    // still has the just-committed string from the previous onChange.
    const current = (valueRef.current || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const next = current.includes(label)
      ? current.filter((s) => s !== label)
      : [...current, label];
    const joined = next.join(", ");
    // Update the ref synchronously so the next click within the same
    // render frame sees the correct state. React will reconcile.
    valueRef.current = joined;
    onChange(joined);
  };

  // Industry-specific icon + color palette
  const ICONS = [
    { icon: Clock,      color: "text-amber-400",  bg: "bg-amber-500/10",  border: "border-amber-500/20" },
    { icon: DollarSign, color: "text-red-400",    bg: "bg-red-500/10",    border: "border-red-500/20" },
    { icon: Users,      color: "text-blue-400",   bg: "bg-blue-500/10",   border: "border-blue-500/20" },
    { icon: Zap,        color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/20" },
    { icon: TrendingUp, color: "text-cyan-400",   bg: "bg-cyan-500/10",   border: "border-cyan-500/20" },
    { icon: Sparkles,   color: "text-pink-400",   bg: "bg-pink-500/10",   border: "border-pink-500/20" },
  ];

  const WINDOW_SIZE = 5;
  const all = suggestions || [];
  const [windowStart, setWindowStart] = React.useState(0);
  const [refreshSpin, setRefreshSpin] = React.useState(0);

  // Reset window when the catalog changes (industry switch)
  React.useEffect(() => {
    setWindowStart(0);
  }, [industry]);

  // Visible window — wraps around the catalog so the refresh button can
  // cycle indefinitely. Selected chips from outside the window get unioned
  // in below so the user never loses a previously-tapped pain.
  const windowSlice = React.useMemo(() => {
    if (all.length === 0) return [];
    const slice = [];
    for (let i = 0; i < Math.min(WINDOW_SIZE, all.length); i++) {
      slice.push(all[(windowStart + i) % all.length]);
    }
    return slice;
  }, [all, windowStart]);

  // Always show selected chips, even if they're outside the current window.
  const cards = React.useMemo(() => {
    const out = [...windowSlice];
    for (const s of selected) {
      if (all.includes(s) && !out.includes(s)) {
        out.push(s);
      }
    }
    return out;
  }, [windowSlice, selected, all]);

  const handleRefresh = () => {
    setWindowStart((s) => (s + WINDOW_SIZE) % Math.max(1, all.length));
    setRefreshSpin((n) => n + 1);
  };

  const showRefresh = all.length > WINDOW_SIZE;

  if (all.length === 0) {
    return (
      <p className="text-xs text-slate-500 italic">
        Pick your industry above — we'll show common bottlenecks for businesses like yours.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-slate-400">
          Showing {Math.min(WINDOW_SIZE, all.length)} of {all.length} common pains in{" "}
          <span className="text-slate-300 font-medium">
            {subVertical === "property_management"
              ? "Property Management"
              : (INDUSTRY_NAMES[industry] || "your industry")}
          </span>
        </p>
        {showRefresh && (
          <motion.button
            type="button"
            onClick={handleRefresh}
            whileTap={{ scale: 0.92 }}
            aria-label="Show different problem suggestions"
            title="Don't see yours? Tap to cycle through more"
            className="w-9 h-9 rounded-full bg-slate-950/60 border border-white/10 hover:border-[#3b82f6]/60 hover:bg-[#3b82f6]/10 flex items-center justify-center text-slate-400 hover:text-[#3b82f6] transition-all shadow-sm flex-shrink-0"
          >
            <motion.span
              key={refreshSpin}
              initial={{ rotate: 0 }}
              animate={{ rotate: 360 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              className="inline-flex"
            >
              <RefreshCw className="w-4 h-4" />
            </motion.span>
          </motion.button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" role="group" aria-label="Common problems for your industry">
        <AnimatePresence mode="popLayout" initial={false}>
          {cards.map((label, i) => {
            const active = selected.includes(label);
            const style = ICONS[i % ICONS.length];
            const Icon = style.icon;
            return (
              <motion.button
                key={label}
                layout
                type="button"
                role="checkbox"
                aria-checked={active}
                onClick={() => toggle(label)}
                initial={{ opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.96, transition: { duration: 0.18 } }}
                transition={{ duration: 0.22, delay: i * 0.03 }}
                whileTap={{ scale: 0.98 }}
                title={label}
                className={`group relative overflow-hidden rounded-xl border px-3 sm:px-4 py-3.5 text-left transition-colors duration-200 min-w-0 ${
                  active
                    ? `${style.bg} ${style.border} border-opacity-80 shadow-md`
                    : "bg-slate-950/40 border-white/10 hover:border-white/25 hover:bg-slate-900/60"
                }`}
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-lg ${style.bg} border ${style.border} flex items-center justify-center flex-shrink-0 ${active ? "ring-2 ring-offset-1 ring-offset-slate-950" : ""}`} style={active ? { "--tw-ring-color": "currentColor" } : {}}>
                    <Icon className={`w-4 h-4 ${style.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold leading-snug break-words ${active ? "text-white" : "text-slate-200"}`}>
                      {label}
                    </p>
                    <p className={`text-[11px] mt-0.5 ${active ? style.color + " opacity-90" : "text-slate-500"}`}>
                      {active ? "✓ Selected" : "Tap to add"}
                    </p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    active
                      ? `${style.color} border-current bg-current/10`
                      : "border-slate-600 group-hover:border-slate-400"
                  }`}>
                    {active && <Check className={`w-3 h-3 ${style.color}`} strokeWidth={3} />}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </AnimatePresence>
      </div>

      {selected.length > 0 && (
        <p className="text-[11px] text-blue-400 font-medium flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {selected.length} {selected.length === 1 ? "problem" : "problems"} selected — we'll match tools to each one
        </p>
      )}
    </div>
  );
};

/**
 * AnimatedCount — smooth count-up between two integer values using rAF.
 * Easing: ease-out cubic (faster start, settles softly). Duration scales
 * with delta so a $5K → $200K jump takes longer than a $5K → $7K nudge,
 * which feels more honest than a fixed-duration tween. tabular-nums
 * styling on the parent keeps digit width fixed so the layout doesn't
 * jump as digits change.
 */
function AnimatedCount({ value, duration }) {
  // Coerce non-finite (NaN / undefined / null from a stale catalog response)
  // to 0 so .toLocaleString() can never throw on the render path.
  const safeValue = Number.isFinite(value) ? value : 0;
  const [display, setDisplay] = useState(safeValue);
  const fromRef = useRef(safeValue);
  useEffect(() => {
    const from = fromRef.current;
    const to = safeValue;
    if (from === to) {
      setDisplay(to);
      return undefined;
    }
    const dur = duration ?? Math.min(900, Math.max(280, Math.abs(to - from) / 90));
    const t0 = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = Math.round(from + (to - from) * eased);
      setDisplay(current);
      if (t < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [safeValue, duration]);
  return <>{display.toLocaleString()}</>;
}

/**
 * LiveOpportunityCounter — hero stat card on Step 3. Sums median_loss
 * across selected problem chips × 12, counts up live as chips are
 * tapped. Anchored to PROBLEM_NODES.median_loss_per_month — buyer's
 * proof that the report is calibrated to real industry data, not a
 * theatrical inflation. Hidden when no chips selected (prevents a
 * confusing $0 banner pre-interaction).
 *
 * Visual register matches the report's hero stat block (gradient
 * border + accent line + tabular-nums big number) so the form and
 * the post-payment report read as one continuous document, not two
 * different surfaces. Emerald accent because the number signals
 * "money on the table" — distinct from the form's blue informational
 * tone.
 */
function LiveOpportunityCounter({ annual, monthly, matchedCount, selectedCount, industryLabel }) {
  if (selectedCount === 0) return null;
  const isMatched = matchedCount > 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="relative overflow-hidden rounded-2xl border border-emerald-500/30 mb-3"
      style={{
        background: "linear-gradient(135deg, rgba(16,185,129,0.10), rgba(15,23,42,0.5))",
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent, rgba(16,185,129,0.7), transparent)" }}
      />
      <div className="p-5 md:p-6">
        <div className="flex items-baseline justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <motion.span
              animate={{ scale: [1, 1.4, 1], opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              className="w-1.5 h-1.5 bg-emerald-400 rounded-full inline-block"
            />
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-300">
              Live AI Opportunity
            </p>
          </div>
          <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 hidden sm:block">
            Updates as you click
          </p>
        </div>
        <div className="flex items-baseline flex-wrap gap-x-2.5 gap-y-1 mb-2">
          <span className="text-4xl md:text-5xl font-black text-white tracking-tight tabular-nums leading-none">
            $<AnimatedCount value={annual} />
          </span>
          <span className="text-sm text-slate-300 font-medium pb-0.5">/year recoverable</span>
        </div>
        <p className="text-xs text-slate-400">
          <span className="text-emerald-400 font-semibold">{selectedCount}</span>{" "}
          problem{selectedCount !== 1 ? "s" : ""} selected ·{" "}
          <span className="text-slate-300 font-medium tabular-nums">
            $<AnimatedCount value={monthly} />/mo
          </span>{" "}
          median across {industryLabel} businesses
          {isMatched && (
            <span className="text-slate-500">
              {" "}· {matchedCount} precise match{matchedCount !== 1 ? "es" : ""}, rest tightened at submit
            </span>
          )}
        </p>
      </div>
    </motion.div>
  );
}

const AuditFormPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { lang, t } = useContext(LanguageContext);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitElapsed, setSubmitElapsed] = useState(0);
  const [submitAttempt, setSubmitAttempt] = useState(1); // 1, 2, or 3 — drives retry banner
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  // R29.3 (Sarah verdict — clicking audit from /real-estate landing
  // page dropped the buyer into a generic 16-industry form with no
  // signal they were on a real-estate-specific path). Track whether
  // industry came from a vertical landing page so we can render
  // vertical-aware branding + collapsed industry confirmation card
  // instead of the full 16-industry grid.
  // Capture the ORIGINAL preset value so the subtext stays honest if
  // the buyer changes their industry mid-form.
  const [originalPresetIndustry] = useState(() => searchParams.get("industry") || "");
  const [industryPreset] = useState(() => Boolean(searchParams.get("industry")));
  // Allow buyer to expand the picker if the URL-detected industry is wrong.
  const [industryPickerExpanded, setIndustryPickerExpanded] = useState(false);
  // Round-21.1: live tool catalog count for the loader copy. Avoids the
  // off-by-N drift Sarah caught when the static "X tools" string lagged
  // the actual catalog (348 → 343 mismatch in R21 walk). Falls back to 348
  // when /api/tools/stats is unreachable so loader copy never blanks.
  const [liveToolCount, setLiveToolCount] = useState(348);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://ai-workshop-llc-backend.onrender.com'}/api/tools/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.total) setLiveToolCount(d.total); })
      .catch(() => {});
  }, []);
  const [formData, setFormData] = useState(() => {
    const base = loadSavedFormData();
    // URL params from Shopify/landing pages override saved data. If the URL
    // switches the industry/platform, also wipe problems/goals/currentTools —
    // otherwise selections from a prior industry leak into the new flow (same
    // bug the reviewer hit on a cleared-localStorage Ecommerce session where
    // Real Estate goals appeared in the payload).
    const industry = searchParams.get("industry");
    const businessType = searchParams.get("businessType");
    const platform = searchParams.get("platform");
    // R31.7d: sub_vertical hint from PM landing page (and any future
    // sub-vertical landing pages — dental specialties, healthcare
    // sub-types, etc.). Stamps onto the form so the audit submission
    // forwards it to the engine; the FCRA fence + cluster taxonomy +
    // PM cluster routing all read it for context disambiguation.
    const subVertical = searchParams.get("sub_vertical");
    if (industry || businessType || platform || subVertical) {
      const industryChanged = industry && industry !== base.industry;
      const typeChanged = businessType && businessType !== base.businessType;
      const scopeReset = (industryChanged || typeChanged)
        ? { problems: "", goals: "", currentTools: "" }
        : {};
      return {
        ...base,
        ...scopeReset,
        ...(industry && { industry }),
        ...(businessType && { businessType }),
        ...(platform && { ecommercePlatform: platform }),
        ...(subVertical && { subVertical }),
      };
    }
    return base;
  });
  const [prioritySupport, setPrioritySupport] = useState(false);

  const goalsRef = useRef(null);
  // If problems are already filled (restored from localStorage), skip the auto-scroll
  const hasScrolledToGoals = useRef(formData.problems.trim().length >= 10);

  useEffect(() => {
    if (
      step === 3 &&
      formData.problems.trim().length >= 10 &&
      !hasScrolledToGoals.current &&
      goalsRef.current
    ) {
      hasScrolledToGoals.current = true;
      setTimeout(() => {
        goalsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 450);
    }
    if (step !== 3) {
      hasScrolledToGoals.current = false;
    }
  }, [formData.problems, step]);

  const [validation, setValidation] = useState({
    businessName: { touched: false, valid: false, message: "" },
    industry: { touched: false, valid: false, message: "" },
    businessSize: { touched: false, valid: false, message: "" },
    budget: { touched: false, valid: false, message: "" },
    businessType: { touched: false, valid: false, message: "" },
    ecommercePlatform: {
      touched: false,
      valid: true,
      message: "",
    }, // conditionally required
    aiAssistant: { touched: false, valid: false, message: "" },
    problems: { touched: false, valid: false, message: "" },
    email: { touched: false, valid: false, message: "" },
  });

  // ---------- Analytics tracking ----------
  useEffect(() => {
    if (typeof window.gtag !== "undefined") {
      window.gtag("event", "form_step_view", {
        step_number: step,
        industry: formData.industry || "not_selected",
        businessType: formData.businessType || "not_selected",
        language: lang,
      });
    }
  }, [step, formData.industry, formData.businessType, lang]);

  // Track validation errors only on step navigation / submit, not on every field blur
  const trackValidationErrors = (errorFields) => {
    if (errorFields.length > 0 && typeof window.gtag !== "undefined") {
      window.gtag("event", "form_validation_errors", {
        error_fields: errorFields.join(","),
        step: step,
        language: lang,
      });
    }
  };

  // ---------- Local storage ----------
  useEffect(() => {
    localStorage.setItem("auditFormData", JSON.stringify(formData));
  }, [formData]);

  // ---------- UTM + gclid attribution capture ----------
  // R31.7e: shared captureAttribution() util now handles utm_* +
  // referrer + gclid + fbclid + msclkid + landing_page. Same util is
  // called on every paid-launch landing page (PM/dental/RE/HVAC) so a
  // buyer who lands on /pm?gclid=abc and bounces around still ships
  // the gclid on their /audit submission.
  useEffect(() => {
    captureAttribution();
  }, [searchParams]);

  // Suggestions computed instantly from inline data — no network call, no cold-start latency
  // R35: pass subVertical so /pm visitors get PM-specific chips instead of
  // the brokerage-flavored real_estate pool.
  const suggestions = React.useMemo(
    () => getSuggestions(formData.industry, formData.businessType, formData.subVertical),
    [formData.industry, formData.businessType, formData.subVertical]
  );

  // ---------- Backend problem catalog (for chip-ID wire-up) ----------
  // The marketing-curated chip labels in INDUSTRY_SUGGESTIONS are user-facing,
  // but the backend rule engine wants stable problem IDs from PROBLEM_NODES so
  // it can skip the free-text re-match step that produced "tenant screening"
  // / "maintenance requests" leaking into Real Estate reports. We fetch the
  // canonical catalog on industry change, then match selected labels to IDs
  // by token overlap at submit time. Cache is keyed by canonical industry.
  const [problemCatalog, setProblemCatalog] = useState({}); // { industryKey: [{id, problem_text, ...}] }

  // Per-field chip expansion state — drives "Show more" affordance on the
  // 12-chip-deep goal/currentTools suggestion strips. Default collapsed (6
  // visible); user clicks to expand to all 18. Keyed by field so goals can
  // be expanded while currentTools stays collapsed, etc.
  const [chipExpansion, setChipExpansion] = useState({});
  const toggleChipExpansion = (field) =>
    setChipExpansion((prev) => ({ ...prev, [field]: !prev[field] }));
  useEffect(() => {
    const ind = (formData.industry || "").trim();
    if (!ind || problemCatalog[ind]) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/audit/problems/${encodeURIComponent(ind)}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const list = Array.isArray(data?.problems) ? data.problems : [];
        if (!cancelled) setProblemCatalog((prev) => ({ ...prev, [ind]: list }));
      } catch (_) {
        // Network blip — submit will just send no problemIds and the backend
        // falls back to its existing free-text path. Non-fatal.
      }
    })();
    return () => { cancelled = true; };
  }, [formData.industry, problemCatalog]);

  // (Replaced: a static revenue×industry-percentage hint used to live here.
  // Superseded by LiveOpportunityCounter below, which ticks up live against
  // real PROBLEM_NODES.median_loss data instead of a theatrical estimate.)

  // ─── Live AI Opportunity counter ──────────────────────────────────────────
  // Sums median_loss_per_month across the buyer's selected problem chips
  // (resolved via mapLabelsToProblemIds → catalog), × 12 = visible annualized
  // opportunity that ticks up live as more chips get clicked. Anchored to
  // real PROBLEM_NODES data — not theatrical inflation. The buyer is
  // watching dollars accumulate as they tell the truth, which is the stake-
  // raising moment that converts "audit form" into "watch your blueprint
  // take shape." Returns 0s when no chips are selected (counter hidden).
  //
  // The catalog→byId Map is memoized separately so it only rebuilds when the
  // industry or catalog payload changes. The hot-path memo (recomputed on
  // every chip click) just sums against the cached map.
  const _catalogById = React.useMemo(() => {
    const ind = (formData.industry || "").trim();
    return new Map((problemCatalog[ind] || []).map((p) => [p.id, p]));
  }, [formData.industry, problemCatalog]);

  const liveAnnualOpportunity = React.useMemo(() => {
    const ind = (formData.industry || "").trim();
    const catalog = problemCatalog[ind] || [];
    const allTokens = (formData.problems || "")
      .split(",").map((s) => s.trim()).filter(Boolean);
    if (!_catalogById.size || !allTokens.length) {
      return { monthly: 0, annual: 0, matchedCount: 0, selectedCount: allTokens.length };
    }

    // Phrase + token-overlap matcher — high precision, low recall.
    const ids = mapLabelsToProblemIds(allTokens, catalog, formData.subVertical);
    let monthly = 0;
    for (const id of ids) {
      const p = _catalogById.get(id);
      if (p && typeof p.median_loss_per_month === "number") {
        monthly += p.median_loss_per_month;
      }
    }

    // Fallback for unmatched chips. The frontend matcher is intentionally
    // strict (it only ships precise IDs to the backend at submit), but the
    // backend's free-text matcher catches the rest at submit time via the
    // chip-ID + free-text union landed in commit 49c968a. So 15 selected
    // chips with only 1 phrase-matched would otherwise show ~$150K instead
    // of the ~$1.5M the buyer will actually see post-submit. Fix: credit
    // each unmatched chip at the industry's median problem loss (the
    // catalog's per-problem median averaged across that industry's nodes).
    // Honest, conservative — matches what the backend's union path will
    // surface, just earlier and slightly under-inflated to avoid
    // over-promising on the unlock page.
    const unmatched = allTokens.length - ids.length;
    if (unmatched > 0) {
      const losses = catalog
        .map((p) => p.median_loss_per_month)
        .filter((v) => typeof v === "number" && v > 0);
      if (losses.length) {
        const median = losses.slice().sort((a, b) => a - b)[Math.floor(losses.length / 2)];
        // 0.7× discount on the fallback so phrase-matched (precise) chips
        // count more than fallback (estimated) chips. Honest signal that
        // the precise match is more confident than the estimate.
        monthly += unmatched * median * 0.7;
      }
    }

    // Business-size scaling. The catalog's median_loss_per_month is calibrated
    // for an "average SMB" (≈ medium tier). A solo founder or 1-5-person
    // shop physically can't have the same monthly loss as a 20-person store —
    // their revenue base is smaller. QA caught: a 1-5-person ecommerce buyer
    // saw a $281K/year live counter against problems whose median is built
    // for medium-sized stores. Skeptical buyers read "$281K recoverable"
    // and instantly classify the page as theatrical. Scale the headline
    // figure to the buyer's stated size so the number stays plausible.
    const SIZE_SCALE = { solo: 0.20, small: 0.35, medium: 0.70, large: 1.0 };
    const scale = SIZE_SCALE[(formData.businessSize || "").trim()] ?? 0.5;
    monthly *= scale;

    // Hard ceiling tied to stated budget — recoverable should never exceed
    // ~12× monthly AI budget (already an aggressive ROI claim). Keeps the
    // counter believable for buyers picking the cheapest tier.
    const BUDGET_MONTHLY_MAX = {
      "under-200": 2400, "under-500": 6000, "under-2k": 24000,
      "2k-5k": 60000, "5k-10k": 120000, "10k-25k": 300000, "25k-plus": Infinity,
    };
    const ceiling = BUDGET_MONTHLY_MAX[(formData.budget || "").trim()] ?? Infinity;
    if (monthly > ceiling) monthly = ceiling;

    return {
      monthly: Math.round(monthly),
      annual: Math.round(monthly * 12),
      matchedCount: ids.length,
      selectedCount: allTokens.length,
    };
  }, [formData.industry, formData.problems, formData.businessSize, formData.budget, problemCatalog, _catalogById]);

  // ---------- Validation ----------
  const validateField = (field, value) => {
    let valid = true;
    let message = "";

    switch (field) {
      case "businessName":
        valid = value.trim().length >= 2;
        message = valid
          ? ""
          : t("audit.errorBusinessName");
        break;
      case "industry":
        valid = !!value;
        message = valid ? "" : t("audit.errorIndustry");
        break;
      case "businessSize":
        valid = !!value;
        message = valid
          ? ""
          : t("audit.errorBusinessSize");
        break;
      case "budget":
        valid = !!value;
        message = valid ? "" : t("audit.errorBudget");
        break;
      case "businessType":
        valid = !!value;
        message = valid
          ? ""
          : t("audit.errorBusinessType");
        break;
      case "ecommercePlatform":
        // Only required when both (a) industry is commerce-shaped AND (b) the
        // business type implies a storefront. Without the industry guard the
        // field would be hidden in the UI for non-commerce industries while
        // still blocking submit — a manufacturer who picked businessType="both"
        // (B2B products + services) couldn't get past step 2.
        if (
          (formData.industry === "ecommerce" ||
            formData.industry === "retail") &&
          (formData.businessType === "ecommerce" ||
            formData.businessType === "both")
        ) {
          valid = !!value;
          message = valid
            ? ""
            : t("audit.errorEcommercePlatform");
        } else {
          valid = true;
          message = "";
        }
        break;
      case "aiAssistant":
        valid = !!value;
        message = valid
          ? ""
          : t("audit.errorAiAssistant");
        break;
      case "problems":
        valid = value.trim().length >= 10;
        message = valid
          ? ""
          : t("audit.errorProblems");
        break;
      case "email": {
        const emailRegex =
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        valid = emailRegex.test(value);
        message = valid ? "" : t("audit.errorEmail");
        break;
      }
      default:
        break;
    }

    setValidation((prev) => ({
      ...prev,
      [field]: { ...prev[field], valid, message },
    }));

    return valid;
  };

  const handleChange = (field, value) => {
    // QA caught: a buyer who triggered "Please select your industry" once
    // (e.g. by clicking Continue with nothing selected) saw the error
    // PERSIST across the next card click on slow renders, and reported
    // "I had to click twice on the E-commerce card." Clearing the error
    // synchronously the moment a non-empty value arrives eliminates the
    // perceived double-click — even if the validateField setValidation
    // hasn't propagated yet.
    if (value) {
      setValidation((prev) => prev[field]?.message
        ? { ...prev, [field]: { ...prev[field], valid: true, message: "" } }
        : prev
      );
    }
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // When industry changes, wipe the three industry-scoped fields so
      // selections from a previous industry can't leak into the new one.
      // Without this, a Legal user who switches to Real Estate can send a
      // "Document review" problem id that doesn't exist in the RE taxonomy,
      // which can crash the report generator downstream.
      if (field === "industry" && prev.industry !== value) {
        if (prev.industry) {
          next.problems = "";
          next.goals = "";
          next.currentTools = "";
          // businessType, ecommercePlatform also bound to industry context —
          // an Ecommerce user switching to Real Estate shouldn't still have
          // businessType="ecommerce" and ecommercePlatform="shopify" selected.
          next.businessType = "";
          next.ecommercePlatform = "";
        }
        // SMART DEFAULTS — pre-fill businessType + budget with industry-typical
        // values so the form feels like "confirm what we already suspect"
        // instead of a blank interrogation. User can change in Step 2; we only
        // set fields that are currently empty so we never overwrite a real pick.
        const _defaults = INDUSTRY_DEFAULTS[value];
        if (_defaults) {
          if (!next.businessType) next.businessType = _defaults.businessType || "";
          if (!next.budget)       next.budget       = _defaults.budget       || "";
        }
      }
      return next;
    });
    if (validation[field]?.touched) {
      validateField(field, value);
    }
  };

  const handleBlur = (field) => {
    setValidation((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        touched: true,
      },
    }));
    validateField(field, formData[field]);
  };

  const toggleSuggestion = (field, suggestion) => {
    // Suppress auto-scroll to goals when clicking a suggestion chip
    if (field === 'problems') hasScrolledToGoals.current = true;
    setFormData((prev) => {
      const current = prev[field]
        ? prev[field]
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
      const updated = current.includes(suggestion)
        ? current.filter((item) => item !== suggestion)
        : [...current, suggestion];

      const newValue = updated.join(", ");

      if (typeof window.gtag !== "undefined") {
        window.gtag("event", "suggestion_selected", {
          field,
          suggestion,
          industry: formData.industry,
          businessType: formData.businessType,
          language: lang,
        });
      }

      return { ...prev, [field]: newValue };
    });
  };

  const clearSuggestions = (field) => {
    setFormData((prev) => ({ ...prev, [field]: "" }));
  };

  // ---------- Step navigation ----------
  // R44 (Maya verdict): "industry click race" — when the buyer clicks
  // the industry tile and immediately clicks Continue, the
  // handleChange("industry", v) setFormData is still in React's
  // pending-update queue. nextStep reads formData.industry from the
  // closure (stale empty string), validation fails, error toast fires.
  // formDataRef is updated via useEffect on every formData change and
  // gives nextStep a guaranteed-fresh read path. The original
  // formData is still used for non-race-prone validations.
  const formDataRef = React.useRef(formData);
  React.useEffect(() => { formDataRef.current = formData; }, [formData]);

  const nextStep = () => {
    let isValid = true;
    // Snapshot the latest committed form state at click time. If a
    // setFormData fired in the same tick as this Continue click,
    // formDataRef.current may already reflect it (React 18 batches
    // but the ref-write in our effect runs on commit, which often
    // beats the validation-read on the same microtask).
    const fd = formDataRef.current || formData;

    if (step === 1) {
      const fields = ["businessName", "industry"];
      fields.forEach((field) => {
        setValidation((prev) => ({
          ...prev,
          [field]: {
            ...prev[field],
            touched: true,
          },
        }));
        const fieldValid = validateField(
          field,
          fd[field]
        );
        if (!fieldValid) isValid = false;
      });
    } else if (step === 2) {
      // Only businessSize and budget are required to proceed
      // businessType and aiAssistant have sensible defaults
      const fields = [
        "businessSize",
        "budget",
      ];
      fields.forEach((field) => {
        setValidation((prev) => ({
          ...prev,
          [field]: {
            ...prev[field],
            touched: true,
          },
        }));
        const fieldValid = validateField(
          field,
          fd[field]
        );
        if (!fieldValid) isValid = false;
      });
      if (
        fd.businessType === "ecommerce" ||
        fd.businessType === "both"
      ) {
        setValidation((prev) => ({
          ...prev,
          ecommercePlatform: {
            ...prev.ecommercePlatform,
            touched: true,
          },
        }));
        const fieldValid = validateField(
          "ecommercePlatform",
          fd.ecommercePlatform
        );
        if (!fieldValid) isValid = false;
      }
    } else if (step === 3) {
      setValidation((prev) => ({
        ...prev,
        problems: {
          ...prev.problems,
          touched: true,
        },
      }));
      isValid = validateField(
        "problems",
        fd.problems
      );
    }

    if (!isValid) {
      const errorFields = Object.entries(validation).filter(([_, v]) => v.touched && !v.valid).map(([k]) => k);
      trackValidationErrors(errorFields);
      // Name the specific field(s) that are blocking — "Please fill all required fields"
      // was the #1 complaint in the external review for this form.
      const _labelMap = {
        businessName: "Business name",
        industry: "Industry",
        businessSize: "Business size",
        budget: "Budget",
        businessType: "Business type",
        ecommercePlatform: "E-commerce platform",
        problems: "Your biggest challenge",
        email: "Email",
      };
      const missing = errorFields.map((f) => _labelMap[f] || f);
      const msg = missing.length === 1
        ? `Please fill: ${missing[0]}`
        : missing.length > 1
          ? `Please fill: ${missing.join(", ")}`
          : t("audit.errorFillAll");
      toast.error(msg);
      // R44 (Maya verdict): Maya hit Continue on Step 2 without scrolling,
      // got a "Please fill..." error but the missing field
      // (E-commerce platform) was off-screen. Industry-standard fix:
      // scroll the first invalid field into view + focus it. We tag
      // each required field's wrapper with data-field={fieldName}
      // already (see renderFieldWithValidation); just query + scroll.
      if (errorFields.length > 0) {
        const firstError = errorFields[0];
        try {
          // Try the data-field anchor first (most reliable); fall back
          // to any [name=field] input as the secondary anchor.
          const target =
            document.querySelector(`[data-field="${firstError}"]`) ||
            document.querySelector(`[name="${firstError}"]`) ||
            document.getElementById(firstError);
          if (target && typeof target.scrollIntoView === "function") {
            target.scrollIntoView({ behavior: "smooth", block: "center" });
            // Focus after scroll lands (smooth scroll is async). 350ms
            // matches the default scroll-into-view duration in most
            // browsers without making the focus delay feel laggy.
            setTimeout(() => {
              const focusable = target.querySelector?.("input, select, textarea, button") || target;
              if (focusable && typeof focusable.focus === "function") {
                focusable.focus({ preventScroll: true });
              }
            }, 350);
          }
        } catch (_) { /* never throw on scroll */ }
      }
      return;
    }

    // Step-level analytics — fire BEFORE the state update so step var is correct.
    // Lets us measure the funnel: how many users see step N, how many complete N,
    // which optional fields they skip on the way out. Reviewer's flag: without
    // these, we're spending ad money flying blind on form completion rates.
    if (typeof window.gtag !== "undefined") {
      const _optionalSkips = {
        2: ["trajectory","aiAssistant","teamLiteracy","monthlyRevenue","dataReadiness"]
              .filter(f => !formData[f]),
        3: ["hoursWasted","revenueImpact","goals","currentTools","aiConcerns"]
              .filter(f => !formData[f]),
      }[step] || [];

      // Chip-level telemetry — when the user finishes Step 3, capture which
      // problem chips and concern chips they actually selected, plus the
      // length of free-text fields. With chip-IDs (resolved from the
      // backend catalog cached on industry pick) we can answer: which
      // problems convert best to paid? Which chips should be promoted into
      // landing-page H1s vs buried? Without this, ad spend is blind to
      // which pain points are pulling weight.
      let chipMeta = {};
      if (step === 3) {
        const _selectedLabels = (formData.problems || "")
          .split(",").map(s => s.trim()).filter(Boolean);
        const _chipSet = new Set((suggestions?.problems || []).map(p => p.trim()));
        const _selectedChipLabels = _selectedLabels.filter(s => _chipSet.has(s));
        const _ind = (formData.industry || "").trim();
        const _catalog = problemCatalog[_ind] || [];
        const _chipIds = mapLabelsToProblemIds(_selectedChipLabels, _catalog, formData.subVertical);
        // Concerns ship as a comma-joined string in formData.aiConcerns; count
        // chips by counting commas (+1 if non-empty).
        const _concernsRaw = (formData.aiConcerns || "").trim();
        const _concernCount = _concernsRaw
          ? _concernsRaw.split(",").map(s => s.trim()).filter(Boolean).length
          : 0;
        chipMeta = {
          selected_problem_chip_ids: _chipIds.join(",") || "none",
          selected_chip_count:       _selectedChipLabels.length,
          goals_text_length:         (formData.goals || "").trim().length,
          tools_text_length:         (formData.currentTools || "").trim().length,
          concerns_chip_count:       _concernCount,
        };
      }

      window.gtag("event", "form_step_completed", {
        step_number: step,
        industry: formData.industry || "not_selected",
        businessSize: formData.businessSize || "not_selected",
        businessType: formData.businessType || "not_selected",
        skipped_optional_fields: _optionalSkips.join(",") || "none",
        skipped_optional_count: _optionalSkips.length,
        language: lang,
        ...chipMeta,
      });
    }

    setStep((prev) => prev + 1);
    window.scrollTo(0, 0);
  };

  // Accurate completion check per step — used by the stepper so it doesn't
  // lie about "✓ done" when a step was skipped with empty fields.
  const isStepComplete = (stepNum) => {
    if (stepNum === 1) {
      return !!(formData.businessName && formData.businessName.trim().length >= 2)
        && !!formData.industry;
    }
    if (stepNum === 2) {
      const baseOk = !!formData.businessSize && !!formData.budget;
      const platformOk =
        (formData.businessType !== "ecommerce" && formData.businessType !== "both")
        || !!formData.ecommercePlatform;
      return baseOk && platformOk;
    }
    if (stepNum === 3) {
      return (formData.problems || "").trim().length >= 10;
    }
    return true;
  };

  const prevStep = () => {
    // Track backward navigation — high signal for "user got confused on step N
    // and went back to fix something." If this fires often on a specific step,
    // that step's UX needs work.
    if (typeof window.gtag !== "undefined") {
      window.gtag("event", "form_step_back", {
        step_number: step,
        industry: formData.industry || "not_selected",
        language: lang,
      });
    }
    setStep((prev) => prev - 1);
    window.scrollTo(0, 0);
  };

  // ---------- Submit ----------
  const handleSubmit = async () => {
    // Apply defaults for optional fields before validation
    if (!formData.businessType) {
      setFormData(prev => ({ ...prev, businessType: "services" }));
      formData.businessType = "services";
    }
    if (!formData.aiAssistant) {
      setFormData(prev => ({ ...prev, aiAssistant: "chatgpt" }));
      formData.aiAssistant = "chatgpt";
    }

    const requiredFields = [
      "businessName",
      "industry",
      "businessSize",
      "budget",
      "problems",
      "email",
    ];
    if (
      formData.businessType === "ecommerce" ||
      formData.businessType === "both"
    ) {
      requiredFields.push("ecommercePlatform");
    }

    let isValid = true;
    requiredFields.forEach((field) => {
      setValidation((prev) => ({
        ...prev,
        [field]: {
          ...prev[field],
          touched: true,
        },
      }));
      const fieldValid = validateField(
        field,
        formData[field]
      );
      if (!fieldValid) isValid = false;
    });

    if (!isValid) {
      const errorFields = requiredFields.filter(f => !validateField(f, formData[f]));
      trackValidationErrors(errorFields);
      // Build a SPECIFIC "Please fill: Email, Business name" toast instead of
      // the generic "fill all fields" message. Matches the goNext pattern at
      // the top of this file. Earlier behavior bounced buyers back to a step
      // with no clear indicator of WHICH field was missing — direct conversion
      // killer per ad-traffic QA (e.g. email field rendered without a red
      // ring or required marker, so the buyer didn't know what to fix).
      const _humanizedLabels = {
        businessName: "Business name",
        industry: "Industry",
        businessSize: "Business size",
        budget: "Budget",
        businessType: "Business type",
        ecommercePlatform: "E-commerce platform",
        problems: "Your biggest challenge",
        email: "Email",
      };
      const missing = errorFields.map((f) => _humanizedLabels[f] || f);
      const msg = missing.length === 1
        ? `${missing[0]} is required to generate your report`
        : `Please fill: ${missing.join(", ")}`;
      toast.error(msg);
      // Jump to the first step that contains an invalid field
      const fieldStep = {
        businessName: 1, industry: 1,
        businessSize: 2, budget: 2,
        businessType: 2, ecommercePlatform: 2,
        aiAssistant: 2,
        problems: 3, email: 3,
      };
      const firstInvalidStep = requiredFields
        .filter(f => !validateField(f, formData[f]))
        .map(f => fieldStep[f] ?? 1)
        .sort()[0] ?? 1;
      setStep(firstInvalidStep);
      window.scrollTo(0, 0);
      return;
    }

    setIsSubmitting(true);
    setSubmitElapsed(0);
    setSubmitAttempt(1);
    setError(null);

    // Live elapsed counter so the overlay can show motion + switch messages as
    // time passes. A 60+ second wait with a static spinner reads as "dead app".
    const elapsedStart = Date.now();
    const elapsedTicker = setInterval(
      () => setSubmitElapsed(Math.round((Date.now() - elapsedStart) / 1000)),
      1000,
    );

    // Safety timeout — if the whole retry chain hangs, don't leave user staring
    // at a spinner forever. 90s covers three attempts (30s + 2s + 30s + 5s + 30s).
    const submitTimeout = setTimeout(() => {
      setIsSubmitting(false);
      setError("This is taking longer than expected. Your answers are saved — please try again.");
      toast.error("Generation timeout — please try again. Your answers are saved.");
    }, 90000);

    const budgetMapping = {
      "under-2k": "under-5k",
      "2k-5k": "0-5k",
      "5k-10k": "5k-10k",
      "10k-25k": "10k-25k",
      "25k-plus": "25k-50k",
    };

    const sizeMapping = {
      solo: "solo",
      small: "1-5",
      medium: "6-20",
      large: "20-50",
    };

    const mappedData = {
      businessName: formData.businessName.trim(),
      industry: formData.industry,
      // R31.7d: forward sub_vertical hint when set (PM landing page,
      // future sub-vertical landing pages). Backend's cluster taxonomy
      // + FCRA fence + chip resolver use it to disambiguate inside an
      // industry — e.g. real_estate brokerage vs property_management.
      // Empty/undefined when the buyer arrives via a generic path.
      ...(formData.subVertical ? { subVertical: formData.subVertical } : {}),
      businessSize:
        sizeMapping[formData.businessSize] ||
        formData.businessSize ||
        "1-5",
      budget:
        budgetMapping[formData.budget] ||
        formData.budget ||
        "under-5k",
      businessType: formData.businessType,
      ecommercePlatform:
        formData.businessType === "ecommerce" ||
        formData.businessType === "both"
          ? formData.ecommercePlatform || "none"
          : "none",
      aiAssistant: formData.aiAssistant,
      teamLiteracy: formData.teamLiteracy || null,
      problems: [
        formData.problems.trim(),
        formData.hoursWasted ? `Time cost: ${formData.hoursWasted.replace("less-2","<2 hrs/week").replace("2-5","2-5 hrs/week").replace("5-10","5-10 hrs/week").replace("10-plus","10+ hrs/week")}` : null,
        formData.revenueImpact ? `Revenue impact: ${formData.revenueImpact.replace("under-500","<$500/month").replace("500-2k","$500-$2k/month").replace("2k-5k","$2k-$5k/month").replace("5k-plus","$5k+/month")}` : null,
      ].filter(Boolean).join(". "),
      goals:
        formData.goals.trim() ||
        "Not specified",
      currentTools:
        formData.currentTools.trim() ||
        "None specified",
      email: formData.email.trim(),
      prioritySupport,
      language: lang,
      monthlyRevenue: formData.monthlyRevenue || null,
      dataReadiness: formData.dataReadiness || null,
      aiConcerns: formData.aiConcerns.trim() || null,
      trajectory: formData.trajectory || null,
      problemIds: undefined,
    };

    // Resolve selected chip labels to backend problem IDs. Selected labels
    // are the comma-joined entries in formData.problems that match a chip in
    // `suggestions.problems` (toggled via ProblemCards). Free-text the user
    // typed into the textarea is intentionally NOT matched — those go through
    // the backend's free-text matcher, which is fine. Only the chip-clicked
    // pains get IDs, eliminating the matcher-drift class of bug for them.
    try {
      // Round-12 (Sarah R11 verdict): naive `,` split fragmented chips
      // that have embedded commas inside parens
      // (e.g. "Kitchen-to-front comms break down at peak (delays, comp meals)")
      // — chip text didn't match suggestions.problems → 3 selected chips
      // collapsed to 1 ID at backend. Backend log "[CHIP_IDS] resolved
      // 1/1 submitted IDs" was the smoking gun. Now using a paren-aware
      // split that mirrors the backend's _split_chip_string helper so
      // chip resolution survives embedded-comma chips.
      const _splitChipString = (s) => {
        const out = [];
        let depth = 0;
        let buf = "";
        for (const ch of (s || "")) {
          if (ch === "(") { depth++; buf += ch; }
          else if (ch === ")") { depth = Math.max(0, depth - 1); buf += ch; }
          else if (ch === "," && depth === 0) { out.push(buf.trim()); buf = ""; }
          else buf += ch;
        }
        if (buf.trim()) out.push(buf.trim());
        return out.filter(Boolean);
      };
      const allTokens = _splitChipString(formData.problems || "");
      const chipSet = new Set((suggestions?.problems || []).map((p) => p.trim()));
      const selectedChipLabels = allTokens.filter((s) => chipSet.has(s));
      const ind = (formData.industry || "").trim();
      const catalog = problemCatalog[ind] || [];
      const ids = mapLabelsToProblemIds(selectedChipLabels, catalog, formData.subVertical);
      if (ids.length > 0) mappedData.problemIds = ids.slice(0, 20);
      // Diagnostic — surfaces the chip-resolution health to anyone
      // running with DevTools open. If this ever logs "labels=3, ids=1"
      // we know the matcher dropped 2.
      if (selectedChipLabels.length !== ids.length && console?.info) {
        console.info(
          `[chip-resolver] ${selectedChipLabels.length} labels selected, ` +
          `${ids.length} IDs resolved (matcher thresholds may have dropped some)`
        );
      }
    } catch (err) {
      if (console?.warn) console.warn("[chip-resolver] failed:", err);
    }

    // Attach UTM attribution captured from the entry URL (read out of the
    // localStorage `attribution` blob set by the mount-time effect above).
    // Backend stores these on the audit_submissions record so the affiliate
    // /launch endpoint can stamp every downstream tool click with the
    // originating ad campaign.
    try {
      const _attr = readAttribution();
      if (_attr.utm_source)   mappedData.utmSource   = _attr.utm_source;
      if (_attr.utm_medium)   mappedData.utmMedium   = _attr.utm_medium;
      if (_attr.utm_campaign) mappedData.utmCampaign = _attr.utm_campaign;
      if (_attr.utm_term)     mappedData.utmTerm     = _attr.utm_term;
      if (_attr.utm_content)  mappedData.utmContent  = _attr.utm_content;
      if (_attr.referrer)     mappedData.referrer    = _attr.referrer;
      // R31.7e: paid-traffic click IDs + landing-page tracking. gclid
      // is the single most important field for tying ad-click → $149
      // purchase 30 days later via Google Ads conversion import. fbclid
      // / msclkid added for forward-compat when Meta + Microsoft Ads
      // come online. landing_page is the path the buyer first hit on
      // aiworkshopllc.com — answers "did /pm convert better than
      // /property-management" without needing UTM tagging.
      if (_attr.gclid)        mappedData.gclid       = _attr.gclid;
      if (_attr.fbclid)       mappedData.fbclid      = _attr.fbclid;
      if (_attr.msclkid)      mappedData.msclkid     = _attr.msclkid;
      if (_attr.landing_page) mappedData.landingPage = _attr.landing_page;
    } catch (_) { /* attribution missing or unparseable — submit without it */ }

    try {
      if (typeof window.gtag !== "undefined") {
        window.gtag(
          "event",
          "audit_submission_started",
          {
            industry: formData.industry,
            businessSize: formData.businessSize,
            businessType: formData.businessType,
            language: lang,
          }
        );
      }

      // Retry policy:
      //   - Retries on network errors (fetch throws) + gateway statuses
      //     (408, 502, 503, 504). These indicate the request probably
      //     didn't hit our handler or Render is cold-starting.
      //   - 429 is deliberately NOT retried here — the backend still
      //     honors it if we somehow flood it, but retrying 429 immediately
      //     just triggers another 429. The X-Client-Retry header below
      //     tells the backend to skip rate-limit counting for retry
      //     attempts, so one logical user action stays at one rate-limit
      //     bucket slot no matter how many retries fire.
      //   - Does NOT retry on 500 — those come from our handler raising on
      //     a real submission problem (e.g. rule engine crash on a bad id).
      //     Retrying would just duplicate learning_data rows without helping.
      //   - Does NOT retry on 4xx (other than 429 which we also don't retry)
      //     — client problem, retry won't fix it.
      // 3 attempts, backoff 0s / 2s / 5s.
      const isRetryableStatus = (s) => [408, 502, 503, 504].includes(s);
      const MAX_ATTEMPTS = 3;
      const BACKOFF_MS = [0, 2000, 5000];

      let response = null;
      let lastErr = null;

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        setSubmitAttempt(attempt);
        if (BACKOFF_MS[attempt - 1] > 0) {
          await new Promise(r => setTimeout(r, BACKOFF_MS[attempt - 1]));
        }
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000);
          // Tell the backend this is a retry of the same logical submission
          // so its rate limiter doesn't count 3 attempts against the user's
          // per-IP quota and lock them out after one transient 502.
          const _headers = { "Content-Type": "application/json" };
          if (attempt > 1) _headers["X-Client-Retry"] = "true";
          response = await fetch(
            `${API_BASE}/api/audit/submit`,
            {
              method: "POST",
              headers: _headers,
              body: JSON.stringify(mappedData),
              signal: controller.signal,
            }
          );
          clearTimeout(timeoutId);

          if (response.ok) break;                 // success
          if (!isRetryableStatus(response.status)) break; // real error — stop retrying
          if (attempt < MAX_ATTEMPTS) {
            toast.info(`Server woke up slow — retrying (attempt ${attempt + 1}/${MAX_ATTEMPTS})…`);
            continue;
          }
        } catch (fetchErr) {
          lastErr = fetchErr;
          if (attempt < MAX_ATTEMPTS) {
            toast.info(`Connection hiccup — retrying (attempt ${attempt + 1}/${MAX_ATTEMPTS})…`);
            continue;
          }
          throw fetchErr;
        }
      }

      if (!response) {
        throw lastErr || new Error("Could not reach the server. Your answers are saved — please try again.");
      }

      if (!response.ok) {
        const errData = await response
          .json()
          .catch(() => ({}));
        throw new Error(
          errData.detail ||
            errData.message ||
            `Server error ${response.status}`
        );
      }

      const result = await response.json();

      if (typeof window.gtag !== "undefined") {
        window.gtag(
          "event",
          "audit_submission_completed",
          {
            reportId: result.reportId,
            language: lang,
          }
        );
      }

      localStorage.removeItem("auditFormData");

      toast.success(t("audit.submitSuccess"));
      navigate(`/report-preview/${result.reportId}`);
    } catch (err) {
      const errMsg =
        err.message ||
        t("audit.submitGenericError");
      setError(errMsg);
      toast.error(errMsg);

      if (typeof window.gtag !== "undefined") {
        window.gtag(
          "event",
          "audit_submission_failed",
          {
            error: errMsg,
            language: lang,
          }
        );
      }
    } finally {
      clearInterval(elapsedTicker);
      clearTimeout(submitTimeout);
      setIsSubmitting(false);
    }
  };

  // ---------- Render helpers ----------
  const renderFieldWithValidation = (
    field,
    label,
    children,
    helperText,
    options = {}
  ) => {
    // `options.optional = true` suppresses the inline "(required)" tag for
    // fields that live inside an explicitly-optional panel — otherwise we
    // get the Step-3 bug where "Sharpen your blueprint (+ Optional)" wraps
    // a field labeled "(required)" and buyers can't tell which is true.
    const { optional = false } = options;
    const fieldId = `field-${field}`;
    const helperId = `${fieldId}-helper`;
    const errorId = `${fieldId}-error`;
    const isValid =
      validation[field]?.touched &&
      validation[field]?.valid;
    const isInvalid =
      validation[field]?.touched &&
      !validation[field]?.valid;

    return (
      <div className="space-y-1" data-field={field}>
        <Label
          htmlFor={fieldId}
          className="text-slate-300"
        >
          {label}
          {!optional && (
            <>
              {" "}
              <span className="text-slate-500">
                {t("audit.required")}
              </span>
            </>
          )}
        </Label>
        <div
          className={`transition-colors duration-200 rounded-lg ${
            isInvalid ? "ring-2 ring-red-500/50" : ""
          }`}
        >
          {React.cloneElement(children, {
            id: fieldId,
            "aria-invalid": isInvalid,
            "aria-describedby":
              `${helperText ? helperId : ""} ${
                isInvalid ? errorId : ""
              }`
                .trim()
                .replace(/\s+$/, "") || undefined,
            className: `${
              children.props.className || ""
            } ${
              isInvalid
                ? "border-red-500 focus:ring-red-500"
                : isValid
                ? "border-green-500 focus:ring-green-500"
                : ""
            }`,
          })}
        </div>
        {helperText && (
          <p
            id={helperId}
            className="text-sm text-slate-400"
          >
            {helperText}
          </p>
        )}
        <AnimatePresence>
          {isInvalid && (
            <motion.p
              id={errorId}
              initial={{ opacity: 0, y: -5 }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              exit={{ opacity: 0 }}
              className="text-sm text-red-400 flex items-center gap-1"
            >
              <AlertCircle className="w-4 h-4" />{" "}
              {validation[field].message}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const renderSuggestionChips = (
    field,
    suggestionsArray
  ) => {
    if (
      !suggestionsArray ||
      suggestionsArray.length === 0
    )
      return null;

    const colorMap = {
      problems: "blue",
      goals: "emerald",
      currentTools: "purple",
    };
    const color = colorMap[field] || "slate";

    const selectedBgMap = {
      blue: "bg-blue-600 border-blue-600 shadow-blue-600/20 text-white",
      emerald:
        "bg-blue-500 border-blue-600 shadow-blue-500/20 text-white",
      purple:
        "bg-purple-600 border-purple-600 shadow-purple-600/20 text-white",
      slate:
        "bg-slate-600 border-slate-500 shadow-slate-600/20 text-white",
    };

    const selectedBg =
      selectedBgMap[color] || selectedBgMap.slate;
    const defaultBg =
      "bg-slate-950/50 hover:bg-white/8 text-slate-300 border-white/15";

    const currentValue = formData[field] || "";
    const selectedValues = currentValue
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    // Default-show 6 chips. "Show more" expands to the full pool (up to 24
    // sourced from getSuggestions). When collapsed, always include any chips
    // the user has already selected from positions 7+ so picks don't vanish.
    const VISIBLE = 6;
    const isExpanded = !!chipExpansion[field];
    const head = suggestionsArray.slice(0, VISIBLE);
    const tail = suggestionsArray.slice(VISIBLE);
    const tailSelected = tail.filter((s) => selectedValues.includes(s));
    const limited = isExpanded ? suggestionsArray : [...head, ...tailSelected];
    const hiddenCount = isExpanded
      ? 0
      : Math.max(0, suggestionsArray.length - VISIBLE - tailSelected.length);

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-wide text-slate-400">
            {t("audit.suggestedLabel")}{" "}
            {field === "currentTools"
              ? t("audit.suggestedTools")
              : t("audit.suggestedGeneric")}
          </p>
          {currentValue && (
            <button
              type="button"
              className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"
              onClick={() =>
                clearSuggestions(field)
              }
            >
              <X className="w-3 h-3" />{" "}
              {t("audit.clearChips")}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 chip-scroll max-h-28 overflow-y-auto pr-1">
          {limited.map((s) => {
            const isSelected =
              selectedValues.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() =>
                  toggleSuggestion(field, s)
                }
                className={`text-xs px-3 py-1.5 rounded-full border shadow-sm transition-all ${
                  isSelected ? selectedBg : defaultBg
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>
        {(hiddenCount > 0 || isExpanded) && suggestionsArray.length > VISIBLE && (
          <button
            type="button"
            onClick={() => toggleChipExpansion(field)}
            className="text-[11px] text-slate-400 hover:text-slate-200 underline-offset-2 hover:underline transition-colors"
            aria-label={isExpanded ? `Show fewer ${field} suggestions` : `Show ${hiddenCount} more ${field} suggestions`}
          >
            {isExpanded
              ? `Show fewer (${suggestionsArray.length - VISIBLE} hidden)`
              : `Show ${hiddenCount} more — don't see yours?`}
          </button>
        )}
      </div>
    );
  };

  // ---------- MAIN RENDER ----------
  // R36: sub-vertical override so PM buyers see "Property Management" in
  // every render path that reads currentIndustryLabel (proof strip,
  // confirmation card, success toast). Without this the bare
  // INDUSTRY_NAMES lookup returns "Real Estate" for /pm visitors.
  const currentIndustryLabel =
    (formData.subVertical === "property_management"
      ? "Property Management"
      : INDUSTRY_NAMES[formData.industry]) ||
    t("audit.industryFallback");
  const currentIndustryIcon =
    (formData.subVertical === "property_management"
      ? "🏘️"
      : INDUSTRY_ICONS[formData.industry]) || "✨";

  // Industry-aware accent. Once an industry is picked the form's primary
  // accent shifts (Continue button, stepper active dot) — subtle but
  // signals "this form knows you" and keeps continuity with the landing
  // page they came from. Pre-selection: the site's default blue.
  const accent = INDUSTRY_ACCENT[formData.industry] || DEFAULT_ACCENT;

  const STEP_META = [
    { label: t("audit.step1Title"), icon: Users },
    { label: t("audit.step2Title"), icon: DollarSign },
    { label: t("audit.step3Title"), icon: TrendingUp },
    { label: t("audit.step4Title"), icon: CheckCircle2 },
  ];

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      {/* Dot-grid background */}
      <div
        className="fixed inset-0 opacity-[0.03] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "32px 32px" }}
        aria-hidden="true"
      />
      {/* Generating overlay — progressive stages + retry banner so a 30+ second
          wait doesn't read as "dead app". The stage checklist advances on
          elapsed time (not LLM callbacks, since /audit/submit is synchronous).
          Stage timing spans 0-180s so a Render cold-start (which can stall
          the first request 30-60s before the app even runs) still produces
          a believable "things are happening" feel. Labels mirror the actual
          pipeline order in ai_report_service.py: scoring → matching →
          roadmap → exec summary → playbooks → ROI → finalize. If the
          request finishes at 18s the user simply never sees the later
          stages — they're visual capacity for the worst-case cold start. */}
      <AnimatePresence>
        {isSubmitting && (() => {
          const stages = [
            { label: "Validating your business profile",                  active: 0,   done: 3   },
            { label: `Loading industry benchmarks + ${liveToolCount} tools`, active: 3,   done: 10  },
            { label: "Scoring tools across 7 dimensions for your fit",    active: 10,  done: 25  },
            { label: "Matching tools to your specific problems",          active: 25,  done: 45  },
            { label: "Building your 3-phase implementation roadmap",      active: 45,  done: 75  },
            { label: "Drafting your executive summary",                   active: 75,  done: 105 },
            { label: "Generating playbooks + prompt library",             active: 105, done: 135 },
            { label: "Calculating your ROI projections",                  active: 135, done: 160 },
            { label: "Finalizing your blueprint",                         active: 160, done: 180 },
          ];
          const slowMsg =
            submitElapsed >= 150 ? "Almost there — finalizing your blueprint now."
            : submitElapsed >= 90  ? "Still working — full reports take up to 2-3 minutes during peak."
            : submitElapsed >= 45  ? "Building your roadmap — typically completes within 90 seconds."
            : submitElapsed >= 25  ? "Still working — your blueprint is being personalized."
            : null;
          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-900/95 backdrop-blur-md flex items-center justify-center z-50"
            >
              <div className="text-center max-w-md px-6 w-full">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-500 flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-blue-500/30"
                >
                  <Sparkles className="w-9 h-9 text-white" />
                </motion.div>
                <h2 className="text-2xl font-bold mb-2">Building Your Blueprint…</h2>
                <p className="text-slate-500 text-xs mb-6 tabular-nums">
                  {submitElapsed}s elapsed · typically 30–90s · up to 3 min during peak
                </p>

                {submitAttempt > 1 && (
                  <div className="mb-5 text-xs bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-lg px-3 py-2">
                    Server woke up slow — retrying automatically (attempt {submitAttempt}/3). Your answers are safe.
                  </div>
                )}

                <div className="space-y-2.5 text-left bg-slate-950/60 rounded-xl p-4 border border-white/10">
                  {stages.map((s, i) => {
                    const isDone = submitElapsed >= s.done;
                    const isActive = !isDone && submitElapsed >= s.active;
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center transition-all duration-300 ${
                          isDone ? "bg-[#3b82f6]/20 border border-[#3b82f6]"
                            : isActive ? "bg-[#3b82f6]/10 border border-[#3b82f6]/40"
                            : "bg-slate-800/50 border border-white/5"
                        }`}>
                          {isDone ? (
                            <Check className="w-3 h-3 text-[#3b82f6]" strokeWidth={3} />
                          ) : isActive ? (
                            <motion.div animate={{ scale: [1, 1.4, 1] }} transition={{ duration: 1.2, repeat: Infinity }}
                              className="w-1.5 h-1.5 rounded-full bg-[#3b82f6]" />
                          ) : (
                            <div className="w-1.5 h-1.5 rounded-full bg-slate-600" />
                          )}
                        </div>
                        <span className={`text-sm transition-colors ${
                          isDone ? "text-slate-400 line-through decoration-slate-600 decoration-1"
                            : isActive ? "text-white font-medium"
                            : "text-slate-500"
                        }`}>
                          {s.label}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {slowMsg && (
                  <p className="mt-4 text-xs text-slate-400">{slowMsg}</p>
                )}
              </div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      <Header />

      <main className="pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">

          {/* ── Page header ─────────────────────────────────────── */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 text-[#3b82f6] text-sm px-4 py-1.5 rounded-full mb-4">
              <Sparkles className="w-4 h-4" />
              AI Implementation Audit
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2 font-['Space_Grotesk'] tracking-tight">{t("audit.topTitle")}</h1>
            <p className="text-slate-400 text-base md:text-lg max-w-xl mx-auto">
              {(() => {
                // Strip a leading "your " from the substituted label so the
                // template's "tailored to your {{industry}}" doesn't render
                // as "tailored to your your industry" when the fallback
                // ("your industry") fires before the buyer picks one. QA
                // round 3 caught this on the audit page before chip select.
                //
                // R44 (Maya verdict): naive .toLowerCase() turned
                // "E-commerce & Shopify" into "e-commerce & shopify" —
                // proper noun "Shopify" needs to stay capitalized. Same
                // class of bug applies to "HVAC", "SaaS", "AI", etc. in
                // future label tweaks. Use a proper-noun-aware lowercase
                // helper that only flips letters in non-acronym tokens.
                const PROPER_NOUNS = new Set([
                  "shopify", "hvac", "saas", "ai", "crm", "erp",
                  "woocommerce", "bigcommerce", "magento",
                ]);
                const _smartLower = (s) =>
                  (s || "")
                    .split(/(\s+|&)/)
                    .map((tok) => PROPER_NOUNS.has(tok.toLowerCase().trim())
                      ? tok.trim().replace(/\b\w/g, (c) => c.toUpperCase())
                      : tok.toLowerCase())
                    .join("");
                const _label = _smartLower(currentIndustryLabel).replace(/^your\s+/i, "");
                const _tmpl = t("audit.topSubtitle");
                if (_tmpl) return _tmpl.replace("{{industry}}", _label);
                return `Answer a few focused questions and we'll generate a 3-phase AI roadmap, tool stack, prompt pack, and ROI projections tailored to your ${_label}.`;
              })()}
            </p>
          </div>

          {/* ── Step indicator ───────────────────────────────────── */}
          <div className="mb-8" role="navigation" aria-label="Form progress">
            <div className="flex items-center justify-between max-w-xl mx-auto">
              {STEP_META.map((s, i) => {
                const stepNum = i + 1;
                // "Done" only if we've advanced past the step AND it was actually
                // filled in — prevents the stepper from showing ✓ when the user
                // jumped ahead with empty fields (reviewer-flagged bug).
                const isDone = step > stepNum && isStepComplete(stepNum);
                const isCurrent = step === stepNum;
                const Icon = s.icon;
                return (
                  <React.Fragment key={stepNum}>
                    <div className="flex flex-col items-center gap-1.5 min-w-0">
                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                          isDone || isCurrent
                            ? "text-slate-950"
                            : "bg-slate-900/60 text-slate-500 border border-white/10"
                        }`}
                        style={
                          isDone || isCurrent
                            ? {
                                background: `linear-gradient(135deg, ${accent.hex}, ${accent.hex}d0)`,
                                boxShadow: isCurrent
                                  ? `0 0 0 4px ${accent.hex}30`
                                  : `0 6px 18px -8px ${accent.hex}80`,
                              }
                            : undefined
                        }
                        aria-current={isCurrent ? "step" : undefined}
                      >
                        {isDone ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : (
                          <Icon className="w-4 h-4" />
                        )}
                      </div>
                      <span
                        className={`text-xs hidden sm:block transition-colors ${
                          isCurrent ? "text-white font-medium" : isDone ? "" : "text-slate-500"
                        }`}
                        style={isDone && !isCurrent ? { color: accent.hex } : undefined}
                      >
                        {stepNum === 1 ? "Business" : stepNum === 2 ? "Setup" : stepNum === 3 ? "Challenges" : "Review"}
                      </span>
                    </div>
                    {i < 3 && (
                      <div className="flex-1 mx-2 h-0.5 bg-white/8 overflow-hidden rounded-full">
                        <motion.div
                          className="h-full"
                          style={{ background: accent.hex }}
                          animate={{ width: step > stepNum ? "100%" : "0%" }}
                          transition={{ duration: 0.4, ease: "easeOut" }}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="text-center mt-3">
              <span className="text-xs text-slate-500 flex items-center justify-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {t("audit.estimateMinutes")}
              </span>
            </div>
          </div>

          {/* ── Privacy + trust strip ─────────────────────────────────────
              Step 1 only. After Step 1 the buyer has already committed to
              the form; repeating the privacy promise on every step reads
              as defensive noise rather than reassurance. */}
          {step === 1 && (
            <div className="mb-6 max-w-2xl mx-auto space-y-3">
              {/* R29.3: vertical-aware header. When the buyer arrived from
                  a vertical landing page (?industry=real_estate), make it
                  obvious they're on a vertical-specific path — otherwise
                  the form looks generic and they wonder if they landed
                  on the wrong page. The vertical name + icon + "Built
                  for X businesses" framing matches the landing page's
                  hero copy, so the funnel feels continuous. */}
              {industryPreset && formData.industry && (
                <div className="rounded-2xl border border-blue-500/30 bg-gradient-to-br from-blue-500/10 to-blue-500/5 px-5 py-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl flex-shrink-0" aria-hidden="true">
                      {INDUSTRY_ICONS[formData.industry] || "🏢"}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-blue-300/80">
                        Your AI Implementation Blueprint
                      </p>
                      {/* R35 (Sarah verdict, P0): show "Property Management"
                          when sub_vertical=property_management arrives via
                          /pm or /property-management. Sarah caught the
                          generic "Real Estate" leak — buyer paid CPC on an
                          FCRA-themed ad and saw brokerage framing here. */}
                      <h2 className="text-lg sm:text-xl font-bold text-white truncate">
                        Built for {
                          formData.subVertical === "property_management"
                            ? "property management"
                            : (INDUSTRY_NAMES[formData.industry] || "your industry")
                        } businesses
                      </h2>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3 bg-slate-900/40 border border-white/8 rounded-xl px-4 py-3">
                <ShieldCheck className="w-4 h-4 text-[#3b82f6] mt-0.5 flex-shrink-0" />
                <div className="text-xs text-slate-400 leading-relaxed">
                  <span className="text-white font-semibold">Your answers stay private.</span>{" "}
                  We never sell, share, or resell your data. Skip any question you'd rather keep private —
                  we'll use industry benchmarks to fill the gap.
                </div>
              </div>
            </div>
          )}

          {/* Error banner */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-6 bg-red-900/40 border border-red-500/40 text-red-100 px-4 py-3 rounded-xl flex items-start gap-2"
              >
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Steps with slide animation ───────────────────────── */}
          {/* Dropped AnimatePresence + mode="wait". With mode="wait", the
              outgoing step's exit animation would occasionally get stuck when
              users clicked Back then Continue quickly, leaving the next step's
              wrapper at opacity:0 — the reviewer saw this as Step 4 going
              blank after Back→Continue ("stepper + privacy banner only, no
              Generate button"). Per-step fade-in on mount via `key={step}`
              replays the animation every transition without needing an exit
              choreography — no stuck state possible. */}
          <motion.div
              key={step}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="space-y-10"
            >

            {/* STEP 1 */}
            {step === 1 && (
              <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 shadow-2xl rounded-3xl">
                <CardHeader className="pb-4 border-b border-white/8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <CardTitle className="text-white">{t("audit.step1Title")}</CardTitle>
                      <p className="text-slate-400 text-sm mt-0.5">{t("audit.step1Subtitle")}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {renderFieldWithValidation(
                    "businessName",
                    t("audit.fieldBusinessName"),
                    <Input
                      value={formData.businessName}
                      onChange={(e) =>
                        handleChange(
                          "businessName",
                          e.target.value
                        )
                      }
                      onBlur={() =>
                        handleBlur("businessName")
                      }
                      className="bg-slate-950/50 border-white/10 text-white"
                      placeholder={t(
                        "audit.placeholderBusinessName"
                      )}
                    />,
                    t("audit.helperBusinessName")
                  )}

                  {/* R29.3: vertical-aware UX. When buyer clicked Audit
                      from a vertical landing page (?industry=real_estate),
                      we already know their industry — show a confirmation
                      card with the vertical's icon + name, plus a "change"
                      link, instead of the generic 16-industry grid. The
                      grid only appears when the buyer arrived directly to
                      /audit (no preset) OR clicked the "change" link. */}
                  {industryPreset && !industryPickerExpanded && formData.industry ? (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-300">
                        {t("audit.fieldIndustry")}
                      </label>
                      {/* R31.7e: when sub_vertical=property_management
                          arrives via /pm or /property-management, show
                          "Property Management" rather than the bare
                          "Real Estate" — buyer's first signal that the
                          form recognized their PM-specific intent. */}
                      <div className="flex items-center justify-between gap-3 bg-blue-500/8 border border-blue-500/30 rounded-xl px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-2xl flex-shrink-0" aria-hidden="true">
                            {formData.subVertical === "property_management" ? "🏘️" : (INDUSTRY_ICONS[formData.industry] || "🏢")}
                          </span>
                          <div className="min-w-0">
                            <p className="text-white font-semibold text-sm truncate">
                              {formData.subVertical === "property_management"
                                ? "Property Management"
                                : (INDUSTRY_NAMES[formData.industry] || "Your industry")}
                            </p>
                            <p className="text-xs text-blue-300/80">
                              {formData.industry === originalPresetIndustry
                                ? `Pre-selected from the ${formData.subVertical === "property_management" ? "Property Management" : (INDUSTRY_NAMES[formData.industry] || "industry")} landing page`
                                : `Selected — building your ${formData.subVertical === "property_management" ? "Property Management" : (INDUSTRY_NAMES[formData.industry] || "industry")} blueprint`}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIndustryPickerExpanded(true)}
                          className="text-xs font-semibold text-blue-300 hover:text-blue-200 underline decoration-dotted underline-offset-2 flex-shrink-0"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  ) : (
                    renderFieldWithValidation(
                      "industry",
                      t("audit.fieldIndustry"),
                      <IndustryCardGrid
                        value={formData.subVertical === "property_management" ? "property_management" : formData.industry}
                        onChange={(v) => {
                          // R37: "property_management" is a sub-vertical
                          // dressed up as an industry option for UX
                          // simplicity — internally it submits as
                          // industry=real_estate + subVertical=property_management
                          // so the backend engine matches against the same
                          // tool catalog while the PM chip pool, problem_nodes,
                          // brokerage blocklist, and PM-aware copy all fire.
                          // Picking any other industry clears subVertical
                          // so a buyer who toggled from PM → Restaurant
                          // doesn't carry a stale PM flag.
                          if (v === "property_management") {
                            handleChange("industry", "real_estate");
                            handleChange("subVertical", "property_management");
                          } else {
                            handleChange("industry", v);
                            if (formData.subVertical) {
                              handleChange("subVertical", "");
                            }
                          }
                          // Once the buyer picks (or re-picks), hide the
                          // expanded picker so the confirmation card
                          // re-renders with their choice.
                          if (industryPreset && industryPickerExpanded) {
                            setIndustryPickerExpanded(false);
                          }
                        }}
                        t={t}
                        ariaLabel={t("audit.fieldIndustry")}
                      />,
                      t("audit.helperIndustry")
                    )
                  )}

                  {/* Industry social proof — appears after industry is selected */}
                  {formData.industry && (() => {
                    const INDUSTRY_PROOF = {
                      retail: { stat: "7.2 hrs/week", metric: "saved on average", detail: "Cart recovery + inventory AI", color: "emerald" },
                      hospitality: { stat: "31%", metric: "drop in no-shows", detail: "Reservation + scheduling AI", color: "blue" },
                      professional_services: { stat: "6.4 hrs/week", metric: "freed from admin", detail: "Proposals, intake, and follow-up AI", color: "purple" },
                      technology: { stat: "3.1×", metric: "faster feature shipping", detail: "Code review + documentation AI", color: "cyan" },
                      finance: { stat: "12 hrs/week", metric: "saved on data entry & reconciliation", detail: "AI extraction + auto-categorization", color: "emerald" },
                      manufacturing: { stat: "18%", metric: "reduction in downtime", detail: "Predictive maintenance + scheduling AI", color: "orange" },
                      real_estate: { stat: "9.1 hrs/week", metric: "saved on outreach & CRM", detail: "Lead follow-up + listing AI", color: "blue" },
                      property_management: { stat: "5 days → 2 hours", metric: "tenant screening time", detail: "FCRA-compliant screening + maintenance dispatch AI", color: "emerald" },
                      education: { stat: "4.8 hrs/week", metric: "saved on content creation", detail: "Curriculum + student feedback AI", color: "purple" },
                      dental: { stat: "42%", metric: "fewer no-shows", detail: "Automated reminders + AI answering", color: "blue" },
                      legal: { stat: "8.3 hrs/week", metric: "reclaimed for billable work", detail: "Document review + intake AI", color: "purple" },
                      healthcare: { stat: "2 hrs/day", metric: "saved on clinical documentation", detail: "AI-assisted notes + digital intake", color: "emerald" },
                      hvac: { stat: "35%", metric: "more calls booked", detail: "AI answering + smart scheduling", color: "orange" },
                      accounting: { stat: "14 hrs/week", metric: "saved during tax season", detail: "AI categorization + doc collection", color: "emerald" },
                      ecommerce: { stat: "21%", metric: "cart recovery rate", detail: "AI-timed follow-ups + personalization", color: "blue" },
                      construction: { stat: "3×", metric: "faster estimates", detail: "AI-assisted takeoffs + bid generation", color: "orange" },
                      other: { stat: "5.8 hrs/week", metric: "saved on average", detail: "Across operations, marketing & admin", color: "emerald" },
                    };
                    // R35: subVertical override — /pm visitors see PM
                    // proof stat instead of brokerage's "9.1 hrs/week
                    // saved on outreach & CRM."
                    const _proofKey = (
                      formData.subVertical && INDUSTRY_PROOF[formData.subVertical]
                        ? formData.subVertical
                        : formData.industry
                    );
                    const proof = INDUSTRY_PROOF[_proofKey] || INDUSTRY_PROOF.other;
                    const colors = {
                      emerald: "border-blue-500/30 bg-blue-500/8 text-blue-300",
                      blue: "border-blue-500/30 bg-blue-500/8 text-blue-300",
                      purple: "border-purple-500/30 bg-purple-500/8 text-purple-300",
                      cyan: "border-cyan-500/30 bg-cyan-500/8 text-cyan-300",
                      orange: "border-orange-500/30 bg-orange-500/8 text-orange-300",
                    };
                    const cls = colors[proof.color] || colors.emerald;
                    return (
                      <motion.div
                        key={formData.industry}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`rounded-xl border px-4 py-3 flex items-center gap-3 ${cls}`}
                      >
                        <Zap className="w-4 h-4 flex-shrink-0 opacity-80" />
                        <p className="text-sm">
                          <strong>{formData.subVertical === "property_management" ? "Property Management" : (INDUSTRY_NAMES[formData.industry] || "Your industry")}</strong> businesses in our data average{" "}
                          <strong>{proof.stat}</strong> {proof.metric} — {proof.detail}.
                        </p>
                      </motion.div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 shadow-2xl rounded-3xl">
                <CardHeader className="pb-4 border-b border-white/8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <CardTitle className="text-white">{t("audit.step2Title")}</CardTitle>
                      <p className="text-slate-400 text-sm mt-0.5">{t("audit.step2Subtitle")}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <fieldset className="space-y-6">
                    <legend className="text-lg font-semibold text-white mb-2">
                      {t("audit.step2Group1Title")}
                    </legend>
                    {/* Stacked vertically (was md:grid-cols-2). Side-by-side at
                        half-width caused every budget and size label to truncate
                        ("Solo (1 …", "$2,0…"), which read as a broken form. */}
                    {renderFieldWithValidation(
                      "businessSize",
                      t("audit.fieldBusinessSize"),
                      <SegmentedPicker
                        value={formData.businessSize}
                        onChange={(v) => handleChange("businessSize", v)}
                        columns={4}
                        ariaLabel={t("audit.fieldBusinessSize")}
                        options={[
                          { value: "solo",   label: t("audit.sizeSolo"),   sublabel: "Just me",       icon: Users, iconCount: 1 },
                          { value: "small",  label: t("audit.sizeSmall"),  sublabel: "1–5 people",    icon: Users, iconCount: 2 },
                          { value: "medium", label: t("audit.sizeMedium"), sublabel: "6–20 people",   icon: Users, iconCount: 3 },
                          { value: "large",  label: t("audit.sizeLarge"),  sublabel: "20+ people",    icon: Users, iconCount: 4 },
                        ]}
                      />,
                      t("audit.helperBusinessSize")
                    )}

                    {renderFieldWithValidation(
                      "budget",
                      t("audit.fieldBudget"),
                      <SegmentedPicker
                        value={formData.budget}
                        onChange={(v) => handleChange("budget", v)}
                        ariaLabel={t("audit.fieldBudget")}
                        options={[
                          // QA caught: prior floor of "Under $2,000/mo" disqualified
                          // most solo and 1-5-person Shopify owners — they read it as
                          // "I'm not the customer" and bailed at step 2. Added two
                          // sub-$2K tiers so tight-budget owners feel served. Existing
                          // "10k-25k" tier removed to keep the picker at 6 options
                          // (the UI is grid-constrained); few buyers picked it.
                          { value: "under-200", label: t("audit.budgetUnder200"), sublabel: "Bootstrap",    icon: DollarSign, iconCount: 1 },
                          { value: "under-500", label: t("audit.budgetUnder500"), sublabel: "Side-project", icon: DollarSign, iconCount: 1 },
                          { value: "under-2k",  label: t("audit.budgetUnder2k"),  sublabel: "Starter",      icon: DollarSign, iconCount: 2 },
                          { value: "2k-5k",     label: t("audit.budget2k5k"),     sublabel: "Essentials",   icon: DollarSign, iconCount: 3 },
                          { value: "5k-10k",    label: t("audit.budget5k10k"),    sublabel: "Growth",       icon: DollarSign, iconCount: 4 },
                          { value: "25k-plus",  label: t("audit.budget25kPlus"),  sublabel: "Premium",      icon: DollarSign, iconCount: 5 },
                        ]}
                      />,
                      t("audit.helperBudget")
                    )}
                  </fieldset>

                  <fieldset className="space-y-4 mt-6">
                    <legend className="text-lg font-semibold text-white mb-2">
                      {t("audit.step2Group2Title")}
                    </legend>

                    {renderFieldWithValidation(
                      "businessType",
                      t("audit.fieldBusinessType"),
                      <Select
                        value={formData.businessType}
                        onValueChange={(v) =>
                          handleChange("businessType", v)
                        }
                      >
                        <SelectTrigger className="bg-slate-950/50 border-white/10 text-white h-12">
                          <SelectValue
                            placeholder={t(
                              "audit.placeholderBusinessType"
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f172a] border border-white/10 text-white">
                          {formData.industry === 'retail' ? (
                            <>
                              <SelectItem value="ecommerce">Online store / E-commerce</SelectItem>
                              <SelectItem value="physical">Brick-and-mortar / Physical store</SelectItem>
                              <SelectItem value="both">Physical store + online</SelectItem>
                              <SelectItem value="services">Wholesale / B2B supply</SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="ecommerce">{t("audit.businessTypeEcom")}</SelectItem>
                              <SelectItem value="services">{t("audit.businessTypeServices")}</SelectItem>
                              <SelectItem value="both">{t("audit.businessTypeBoth")}</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>,
                      t("audit.helperBusinessType")
                    )}

                    {/* ecommercePlatform — only renders when the INDUSTRY is
                        commerce-shaped (ecommerce or retail) AND business
                        type is ecommerce/both. QA caught a leak: a
                        manufacturer who picked businessType="both" (meaning
                        "B2B products + services") was being forced to pick
                        a Shopify-equivalent platform with no good answer.
                        Industry guard keeps the question in scope. */}
                    {(formData.industry === "ecommerce" ||
                      formData.industry === "retail") &&
                      (formData.businessType ===
                      "ecommerce" ||
                      formData.businessType ===
                        "both") &&
                      renderFieldWithValidation(
                        "ecommercePlatform",
                        t(
                          "audit.fieldEcommercePlatform"
                        ),
                        <Select
                          value={
                            formData.ecommercePlatform
                          }
                          onValueChange={(v) =>
                            handleChange(
                              "ecommercePlatform",
                              v
                            )
                          }
                        >
                          <SelectTrigger className="bg-slate-950/50 border-white/10 text-white h-12">
                            <SelectValue
                              placeholder={t(
                                "audit.placeholderEcommercePlatform"
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0f172a] border border-white/10 text-white">
                            <SelectItem value="shopify">
                              Shopify
                            </SelectItem>
                            <SelectItem value="woocommerce">
                              WooCommerce
                            </SelectItem>
                            <SelectItem value="bigcommerce">
                              BigCommerce
                            </SelectItem>
                            <SelectItem value="squarespace">
                              Squarespace
                            </SelectItem>
                            <SelectItem value="wix">
                              Wix
                            </SelectItem>
                            <SelectItem value="custom">
                              {t(
                                "audit.platformCustom"
                              )}
                            </SelectItem>
                          </SelectContent>
                        </Select>,
                        t("audit.helperEcommercePlatform")
                      )}
                  </fieldset>

                  {/* OPTIONAL FIELDS — collapsed by default. Reviewer caught
                      that 14 inputs upfront drives 30-50% mid-form abandonment;
                      most users don't change tool recommendations meaningfully
                      based on these. Power-users who want precision can expand. */}
                  <details
                    className="group rounded-xl border border-white/10 bg-slate-950/40 overflow-hidden"
                    onToggle={(e) => {
                      if (e.currentTarget.open && typeof window.gtag !== "undefined") {
                        window.gtag("event", "form_optional_disclosure_opened", {
                          disclosure: "sharpen_your_blueprint",
                          step_number: 2,
                          industry: formData.industry || "not_selected",
                          language: lang,
                        });
                      }
                    }}
                  >
                    <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-2 hover:bg-slate-900/40 transition-colors">
                      <div className="flex items-center gap-2.5">
                        <Plus className="w-4 h-4 text-blue-400 group-open:rotate-45 transition-transform flex-shrink-0" />
                        <div>
                          <div className="text-sm font-medium text-white">Sharpen your blueprint</div>
                          <div className="text-xs text-slate-500">5 optional questions · ~60 seconds · improves ROI accuracy</div>
                        </div>
                      </div>
                      <span className="text-xs text-slate-500 pr-1">+ Optional</span>
                    </summary>
                    <div className="px-4 pb-5 pt-1 space-y-5 border-t border-white/5">

                      <div className="space-y-1">
                        <label className="text-sm font-medium text-slate-300">
                          Where is your business headed?
                        </label>
                        <Select
                          value={formData.trajectory}
                          onValueChange={(v) => handleChange("trajectory", v)}
                        >
                          <SelectTrigger className="bg-slate-950/50 border-white/10 text-white h-12">
                            <SelectValue placeholder="Select trajectory…" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0f172a] border border-white/10 text-white">
                            <SelectItem value="growth">Scaling up — adding customers / locations</SelectItem>
                            <SelectItem value="optimize">Optimizing — same size, better margins</SelectItem>
                            <SelectItem value="pivot">Pivoting — new market or model</SelectItem>
                            <SelectItem value="launch">Just launching — early stage</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {renderFieldWithValidation(
                        "aiAssistant",
                        t("audit.fieldAiAssistant"),
                        <Select
                          value={formData.aiAssistant}
                          onValueChange={(v) =>
                            handleChange("aiAssistant", v)
                          }
                        >
                          <SelectTrigger className="bg-slate-950/50 border-white/10 text-white h-12">
                            <SelectValue
                              placeholder={t(
                                "audit.placeholderAiAssistant"
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0f172a] border border-white/10 text-white">
                            <SelectItem value="chatgpt">
                              ChatGPT
                            </SelectItem>
                            <SelectItem value="grok">
                              Grok
                            </SelectItem>
                            <SelectItem value="claude">
                              Claude
                            </SelectItem>
                            <SelectItem value="gemini">
                              Gemini
                            </SelectItem>
                            <SelectItem value="none">
                              {t(
                                "audit.aiAssistantNone"
                              )}
                            </SelectItem>
                          </SelectContent>
                        </Select>,
                        t("audit.helperAiAssistant"),
                        { optional: true }
                      )}

                      <div className="space-y-3">
                        <Label className="text-slate-300">
                          How comfortable is your team with adopting new technology?
                        </Label>
                        <div className="grid grid-cols-1 gap-2">
                          {[
                            { value: "fast", label: "🟢 We adopt new tools quickly", sub: "Low-friction rollout" },
                            { value: "cautious", label: "🟡 We're cautious but open", sub: "Standard onboarding needed" },
                            { value: "resistant", label: "🔴 Change is difficult for our team", sub: "Recommend 1 tool max in Phase 1" },
                          ].map(({ value, label, sub }) => (
                            <button
                              key={value}
                              type="button"
                              onClick={() => handleChange("teamLiteracy", formData.teamLiteracy === value ? "" : value)}
                              className={`text-left px-4 py-3 rounded-xl border transition-all ${
                                formData.teamLiteracy === value
                                  ? "bg-cyan-500/15 border-cyan-500/50 text-white"
                                  : "bg-slate-950/50 border-white/10 text-slate-300 hover:border-white/20"
                              }`}
                            >
                              <div className="text-sm font-medium">{label}</div>
                              <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Monthly revenue — optional, powers exact ROI */}
                      <div className="space-y-1">
                        <Label className="text-slate-300 text-sm flex items-center gap-1.5">
                          Approximate monthly revenue
                        </Label>
                        <Select
                          value={formData.monthlyRevenue}
                          onValueChange={(v) => handleChange("monthlyRevenue", v)}
                        >
                          <SelectTrigger className="bg-slate-950/50 border-white/10 text-white h-12">
                            <SelectValue placeholder="Select a range — or leave blank" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0f172a] border border-white/10 text-white">
                            <SelectItem value="under-5k">Under $5,000/month</SelectItem>
                            <SelectItem value="5k-15k">$5,000 – $15,000/month</SelectItem>
                            <SelectItem value="15k-50k">$15,000 – $50,000/month</SelectItem>
                            <SelectItem value="50k-150k">$50,000 – $150,000/month</SelectItem>
                            <SelectItem value="150k-plus">$150,000+/month</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500">Sharpens your ROI projections. Skip and we'll use your industry's median.</p>
                      </div>

                      {/* Data readiness — filters plug-and-play vs. pipeline tools */}
                      <div className="space-y-2">
                        <Label className="text-slate-300 text-sm block">
                          Do you have organized customer or transaction data?
                        </Label>
                        <div className="flex flex-col gap-2 pl-1">
                          {[
                            { value: "crm", label: "Yes — in a CRM or database" },
                            { value: "spreadsheets", label: "Yes — in spreadsheets / exports" },
                            { value: "no", label: "Not really — it's scattered or minimal" },
                          ].map(({ value, label }) => (
                            <label key={value} className="flex items-center gap-2.5 cursor-pointer group">
                              <input
                                type="radio"
                                name="dataReadiness"
                                value={value}
                                checked={formData.dataReadiness === value}
                                onChange={() => handleChange("dataReadiness", value)}
                                className="accent-blue-500 w-4 h-4 flex-shrink-0"
                              />
                              <span className={`text-sm transition-colors ${formData.dataReadiness === value ? "text-white" : "text-slate-400 group-hover:text-slate-200"}`}>
                                {label}
                              </span>
                            </label>
                          ))}
                        </div>
                        <p className="text-xs text-slate-500">We use this to match you with plug-and-play tools vs. data-pipeline tools</p>
                      </div>
                    </div>
                  </details>
                </CardContent>
              </Card>
            )}

            {/* STEP 3 */}
            {step === 3 && (
              <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 shadow-2xl rounded-3xl">
                <CardHeader className="pb-4 border-b border-white/8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-[#3b82f6]" />
                    </div>
                    <div>
                      <CardTitle className="text-white">{t("audit.step3Title")}</CardTitle>
                      <p className="text-slate-400 text-sm mt-0.5">{t("audit.step3Subtitle")}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-8">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-6">
                      {/* Live AI Opportunity counter — replaces the static
                          revenue×percentage liveRoiHint. Anchored to real
                          PROBLEM_NODES.median_loss_per_month × 12; ticks up
                          live as the buyer taps chips. Hidden when no chips
                          are selected so we don't show a confusing $0
                          banner pre-interaction. */}
                      <LiveOpportunityCounter
                        annual={liveAnnualOpportunity.annual}
                        monthly={liveAnnualOpportunity.monthly}
                        matchedCount={liveAnnualOpportunity.matchedCount}
                        selectedCount={liveAnnualOpportunity.selectedCount}
                        industryLabel={(formData.subVertical === "property_management" ? "property management" : (INDUSTRY_NAMES[formData.industry] || "similar").toLowerCase())}
                      />
                      {/* PRIMARY input: tappable problem cards. Research-backed:
                          visual multi-select converts 2-3× higher than free-text
                          textareas for B2B forms. Textarea moved below as optional. */}
                      {renderFieldWithValidation(
                        "problems",
                        t("audit.fieldProblems"),
                        <ProblemCards
                          industry={formData.industry}
                          subVertical={formData.subVertical}
                          value={formData.problems}
                          onChange={(v) => {
                            // Suppress the auto-scroll-to-goals effect when
                            // the user clicks a chip card. Clicking a chip
                            // pushes formData.problems past 10 chars, which
                            // would otherwise trip the effect at line ~1170
                            // and yank the page down to the goals section
                            // mid-interaction (reviewer reported this as
                            // "chip-click jumps to bottom of page"). The
                            // existing toggleSuggestion helper already does
                            // this for inline-suggestion clicks; ProblemCards
                            // was bypassing it by calling handleChange direct.
                            hasScrolledToGoals.current = true;
                            handleChange("problems", v);
                          }}
                          suggestions={suggestions.problems}
                          t={t}
                        />,
                        "Tap common problems for your industry. Adds context without the blank-page pressure of a textarea."
                      )}

                      {/* Optional textarea — only shown if the user taps "Describe in your own words" */}
                      <details className="group">
                        <summary className="cursor-pointer list-none inline-flex items-center gap-2 text-xs text-slate-400 hover:text-[#3b82f6] font-medium transition-colors">
                          <Plus className="w-3.5 h-3.5 group-open:rotate-45 transition-transform" />
                          Want to add your own words? (optional)
                        </summary>
                        <div className="mt-3">
                          <Textarea
                            value={formData.problems}
                            onChange={(e) => handleChange("problems", e.target.value)}
                            onBlur={() => handleBlur("problems")}
                            className="bg-slate-950/50 border-white/10 text-white min-h-[100px]"
                            placeholder="Anything specific we should know? (e.g., 'Patients cancel 2-3 hours before and I can't fill the slot')"
                          />
                        </div>
                      </details>

                      <div ref={goalsRef}>
                        <Label className="text-slate-300 mb-1 block">
                          {t("audit.fieldGoals")}
                        </Label>
                        <Textarea
                          value={formData.goals}
                          onChange={(e) =>
                            handleChange(
                              "goals",
                              e.target.value
                            )
                          }
                          className="bg-slate-950/50 border-white/10 text-white min-h-[100px]"
                          placeholder={t(
                            "audit.placeholderGoals"
                          )}
                        />
                        {renderSuggestionChips(
                          "goals",
                          suggestions.goals
                        )}
                      </div>

                      {/* Optional cost-quantifier — moved BELOW goals so its
                          expansion pushes Current Tools / AI Concerns down
                          instead of yanking the goals textarea out from
                          under the user's cursor. QA had flagged "focus
                          seemed to jump unexpectedly when adjacent panels
                          expanded" — the panel sat above goals and its
                          framer-motion expansion caused the layout shift.
                          Gated on problems.length >= 10 so it only
                          appears once the user has named the pain. */}
                      {formData.problems.trim().length >= 10 && (
                        <motion.details
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="group rounded-2xl border border-white/10 bg-slate-950/40 overflow-hidden"
                          onToggle={(e) => {
                            if (e.currentTarget.open && typeof window.gtag !== "undefined") {
                              window.gtag("event", "form_optional_disclosure_opened", {
                                disclosure: "quantify_the_cost",
                                step_number: 3,
                                industry: formData.industry || "not_selected",
                                language: lang,
                              });
                            }
                          }}
                        >
                          <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-2 hover:bg-slate-900/40 transition-colors">
                            <div className="flex items-center gap-2.5">
                              <Plus className="w-4 h-4 text-blue-400 group-open:rotate-45 transition-transform flex-shrink-0" />
                              <div>
                                <div className="text-sm font-medium text-white">Help us quantify the cost</div>
                                <div className="text-xs text-slate-500">Optional · 2 quick picks · sharpens your ROI projection</div>
                              </div>
                            </div>
                            <span className="text-xs text-slate-500 pr-1">+ Optional</span>
                          </summary>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 border-t border-white/5">
                            <p className="col-span-full text-[11px] text-slate-500">
                              Skip either if you don't have a precise number — we'll use industry averages.
                            </p>
                            <div className="space-y-2 col-span-full sm:col-span-1">
                              <Label className="text-slate-300 text-sm">How many hours/week does this cost you?</Label>
                              <SegmentedPicker
                                value={formData.hoursWasted}
                                onChange={(v) => handleChange("hoursWasted", v)}
                                columns={2}
                                ariaLabel="Hours per week wasted"
                                options={[
                                  { value: "less-2",   label: "Under 2 hrs",   sublabel: "Manageable",  icon: Clock, iconCount: 1 },
                                  { value: "2-5",      label: "2–5 hrs",        sublabel: "Annoying",    icon: Clock, iconCount: 2 },
                                  { value: "5-10",     label: "5–10 hrs",       sublabel: "Painful",     icon: Clock, iconCount: 3 },
                                  { value: "10-plus",  label: "10+ hrs",        sublabel: "Critical",    icon: Clock, iconCount: 4 },
                                ]}
                              />
                            </div>
                            <div className="space-y-2 col-span-full sm:col-span-1">
                              <Label className="text-slate-300 text-sm">Approximate monthly revenue impact?</Label>
                              <SegmentedPicker
                                value={formData.revenueImpact}
                                onChange={(v) => handleChange("revenueImpact", v)}
                                columns={2}
                                ariaLabel="Monthly revenue impact"
                                options={[
                                  { value: "under-500",  label: "Under $500",     sublabel: "Minor",     icon: DollarSign, iconCount: 1 },
                                  { value: "500-2k",     label: "$500–$2K",       sublabel: "Noticeable", icon: DollarSign, iconCount: 2 },
                                  { value: "2k-5k",      label: "$2K–$5K",        sublabel: "Significant", icon: DollarSign, iconCount: 3 },
                                  { value: "5k-plus",    label: "$5K+",           sublabel: "Severe",    icon: DollarSign, iconCount: 4 },
                                ]}
                              />
                            </div>
                          </div>
                        </motion.details>
                      )}

                      <div>
                        <Label className="text-slate-300 mb-1 block">
                          {t("audit.fieldCurrentTools")}
                        </Label>
                        <Textarea
                          value={formData.currentTools}
                          onChange={(e) =>
                            handleChange(
                              "currentTools",
                              e.target.value
                            )
                          }
                          className="bg-slate-950/50 border-white/10 text-white min-h-[80px]"
                          placeholder={t(
                            "audit.placeholderCurrentTools"
                          )}
                        />
                        {renderSuggestionChips(
                          "currentTools",
                          suggestions.currentTools
                        )}
                      </div>

                      {/* AI Concerns — feeds risk map section */}
                      <div className="space-y-2 p-4 bg-slate-950/40 border border-white/10 rounded-2xl">
                        <p className="text-xs font-semibold text-amber-400 uppercase tracking-wide flex items-center gap-1.5">
                          <AlertCircle className="w-3.5 h-3.5" />
                          Any concerns about adopting AI?{" "}
                          <span className="text-slate-500 font-normal normal-case tracking-normal">select all that apply</span>
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {[
                            "Data privacy & security",
                            "Integration complexity",
                            "Team adoption",
                            "Cost & ROI uncertainty",
                            "Accuracy & reliability",
                            "Vendor lock-in",
                          ].map((concern) => {
                            const selected = (formData.aiConcerns || "")
                              .split(",").map((s) => s.trim()).filter(Boolean)
                              .includes(concern);
                            return (
                              <button
                                key={concern}
                                type="button"
                                onClick={() => toggleSuggestion("aiConcerns", concern)}
                                className={`text-xs px-3 py-1.5 rounded-full border shadow-sm transition-all ${
                                  selected
                                    ? "bg-amber-600 border-amber-600 text-white"
                                    : "bg-white/5 hover:bg-white/10 text-slate-300 border-white/15"
                                }`}
                              >
                                {concern}
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-xs text-slate-500">These are addressed directly in the Risk Map section of your blueprint</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-slate-950/40 border border-white/10 rounded-2xl p-4 text-sm text-slate-300">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-[#3b82f6]" />
                          <span className="font-semibold">
                            {t("audit.howWeUseTitle")}
                          </span>
                        </div>
                        <p className="text-slate-400 text-xs mb-2">
                          {t("audit.howWeUseIntro")}
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-xs text-slate-400">
                          <li>
                            {t(
                              "audit.howWeUseItem1"
                            )}
                          </li>
                          <li>
                            {t(
                              "audit.howWeUseItem2"
                            )}
                          </li>
                          <li>
                            {t(
                              "audit.howWeUseItem3"
                            )}
                          </li>
                        </ul>
                      </div>

                      {/* Email field — wrapped in renderFieldWithValidation
                          so the failed-validation state shows a red ring + a
                          visible required marker. Earlier the field was a raw
                          <Input> with no required indicator at all, which is
                          why buyers clicked Generate, got bounced back to this
                          step, and couldn't tell what was wrong. */}
                      <div className={`bg-slate-950/40 border rounded-2xl p-4 text-sm transition-colors ${
                        validation.email?.touched && !validation.email?.valid
                          ? "border-red-500/50 ring-2 ring-red-500/30"
                          : "border-white/10"
                      }`}>
                        <Label className="text-slate-300 mb-1 block">
                          {t("audit.fieldEmail")}{" "}
                          <span className="text-red-400 text-xs font-semibold">
                            (required to generate your report)
                          </span>
                        </Label>
                        <Input
                          value={formData.email}
                          onChange={(e) =>
                            handleChange(
                              "email",
                              e.target.value
                            )
                          }
                          onBlur={() =>
                            handleBlur("email")
                          }
                          placeholder={t(
                            "audit.placeholderEmail"
                          )}
                          aria-invalid={validation.email?.touched && !validation.email?.valid}
                          aria-required="true"
                          className={`bg-slate-950/50 text-white transition-colors ${
                            validation.email?.touched && !validation.email?.valid
                              ? "border-red-500/60 focus:border-red-500"
                              : "border-white/10"
                          }`}
                        />
                        {validation.email?.touched && !validation.email?.valid && (
                          <p className="text-xs text-red-400 mt-1.5 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {validation.email?.message || "Enter a valid email address — we send your report there"}
                          </p>
                        )}
                        <p className="text-xs text-slate-500 mt-1">
                          {t("audit.helperEmail")}
                        </p>
                      </div>

                      <div className="bg-slate-950/40 border border-white/10 rounded-2xl p-4 text-xs text-slate-400 flex gap-3">
                        <input
                          id="priority"
                          type="checkbox"
                          checked={prioritySupport}
                          onChange={(e) =>
                            setPrioritySupport(
                              e.target.checked
                            )
                          }
                          className="mt-1 accent-blue-500"
                        />
                        <label
                          htmlFor="priority"
                          className="cursor-pointer"
                        >
                          {t("audit.priorityLabel")}
                        </label>
                      </div>
                    </div>
                  </div>

                </CardContent>
              </Card>
            )}

            {/* STEP 4 (Review & submit) */}
            {step === 4 && (
              <Card
                className="bg-slate-900/70 backdrop-blur-xl border border-white/10 shadow-2xl rounded-3xl overflow-hidden relative"
                style={{ borderColor: `${accent.hex}30` }}
              >
                {/* Top accent line — signals "this is the launch chapter" and
                    carries the industry color forward from earlier steps. */}
                <div
                  className="absolute top-0 left-0 right-0 h-px"
                  style={{ background: `linear-gradient(90deg, transparent, ${accent.hex}, transparent)` }}
                />

                <CardContent className="p-7 md:p-10 space-y-8">
                  {/* ── Editorial header ──────────────────────────────────
                      Replaces the generic "Review your answers" CardHeader
                      with a document-style hero: eyebrow + business name in
                      display weight + industry tag. Gives the buyer the
                      moment of "this is MINE" before they hit Generate. */}
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">
                      Prepared for
                    </p>
                    <h2 className="text-3xl md:text-5xl font-bold text-white leading-[1.05] tracking-tight mb-3">
                      {formData.businessName || t("audit.notSet")}
                    </h2>
                    <p
                      className="inline-flex items-center gap-2 text-sm font-medium px-3 py-1 rounded-full"
                      style={{
                        background: `${accent.hex}1a`,
                        color: accent.hex,
                        border: `1px solid ${accent.hex}40`,
                      }}
                    >
                      <span>{currentIndustryIcon}</span>
                      <span>{currentIndustryLabel} — AI Implementation Blueprint</span>
                    </p>
                  </div>

                  {/* ── Consultant synthesis paragraph ────────────────────
                      One paragraph that synthesizes the whole submission.
                      Reads like a senior consultant briefing the buyer back
                      on what they just said, not a checkout receipt. */}
                  <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl">
                    Based on your inputs, we'll calibrate a phase-1 plan for{" "}
                    <span className="text-white font-semibold">
                      {/* Humanize the size slug — buyer sees "a 1-5 person team",
                          not "for small on a Under $2K monthly budget" which
                          QA flagged as broken grammar. */}
                      {(() => {
                        const _SIZE_LABEL = {
                          solo: "a solo operator",
                          small: "a 1-5 person team",
                          medium: "a 6-20 person team",
                          large: "a 20+ person team",
                        };
                        return _SIZE_LABEL[(formData.businessSize || "").trim()] || "your team";
                      })()}
                    </span>{" "}
                    on{" "}
                    <span className="text-white font-semibold">
                      {/* Article picker: "Under $200" starts with a vowel
                          ("Under" → /ʌn/) so we say "an"; "$500-$2K" starts
                          with a digit which reads as "five hundred dollars"
                          → "a $500-$2K". The $200-$500 tier starts with
                          a digit too. Determine article by the budget label
                          to avoid Sarah's "a Under $200/mo" grammar nit. */}
                      {(() => {
                        if (!formData.budget) return "your stated";
                        const label = formatBudgetRange(formData.budget, "");
                        const article = /^under\s/i.test(label) ? "an" : "a";
                        return `${article} ${label}/mo`;
                      })()}
                    </span>{" "}
                    AI budget, mapped to your{" "}
                    <span className="text-white font-semibold">
                      {liveAnnualOpportunity.selectedCount || 0} priority
                      {liveAnnualOpportunity.selectedCount !== 1 ? " problems" : " problem"}
                    </span>
                    {liveAnnualOpportunity.annual > 0 && (
                      <>
                        {" "}— targeting{" "}
                        <span className="text-white font-semibold tabular-nums">
                          $<AnimatedCount value={liveAnnualOpportunity.annual} />
                        </span>
                        /year in recovered margin
                      </>
                    )}
                    .
                  </p>

                  {/* ── Live opportunity stat block — centerpiece ─────────
                      The same counter from Step 3 but now hero-treated.
                      This is the moment that converts "I filled out a form"
                      into "I see what I'm getting." */}
                  {liveAnnualOpportunity.selectedCount > 0 && (
                    <LiveOpportunityCounter
                      annual={liveAnnualOpportunity.annual}
                      monthly={liveAnnualOpportunity.monthly}
                      matchedCount={liveAnnualOpportunity.matchedCount}
                      selectedCount={liveAnnualOpportunity.selectedCount}
                      industryLabel={(formData.subVertical === "property_management" ? "property management" : (INDUSTRY_NAMES[formData.industry] || "similar").toLowerCase())}
                    />
                  )}

                  {/* ── What you'll receive ─────────────────────────────── */}
                  <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-5">
                    <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-4">
                      What you'll receive
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-3.5">
                      {[
                        { label: "20+ page blueprint", sub: "Tailored to your industry" },
                        { label: "3-phase roadmap", sub: "Week-by-week action plan" },
                        { label: "Tool stack", sub: "Curated for your budget" },
                        { label: "Prompt library", sub: "50+ ready-to-paste prompts" },
                        { label: "ROI projections", sub: "Built from 2026 benchmarks" },
                        { label: "Quick wins", sub: "First action in 24 hrs" },
                      ].map(({ label, sub }) => (
                        <div key={label} className="flex items-start gap-2.5">
                          <CheckCircle2
                            className="w-4 h-4 mt-0.5 flex-shrink-0"
                            style={{ color: accent.hex }}
                          />
                          <div>
                            <p className="text-sm font-semibold text-white leading-tight">{label}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{sub}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ── Profile snapshot — condensed editorial row ────────
                      Replaces the prior 3-column receipt layout. Same data,
                      tighter spacing, eyebrow-style labels so the section
                      reads as document margin notes rather than form fields. */}
                  <div className="border-t border-white/8 pt-6 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-5 text-sm">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1.5">
                        Top priorities
                      </p>
                      <p className="text-slate-300 leading-relaxed">
                        {formData.problems
                          ? formData.problems.split(",").slice(0, 3).map((s) => s.trim()).join(" · ") +
                            (formData.problems.split(",").length > 3
                              ? ` · +${formData.problems.split(",").length - 3} more`
                              : "")
                          : "Not specified"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1.5">
                        Goals
                      </p>
                      <p className="text-slate-300 leading-relaxed">
                        {formData.goals || "Industry benchmarks will fill in"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1.5">
                        Current stack
                      </p>
                      <p className="text-slate-300 leading-relaxed">
                        {formData.currentTools || "Industry defaults will be assumed"}
                      </p>
                    </div>
                  </div>

                  {/* ── Delivery + readiness footer ─────────────────────── */}
                  <div className="border-t border-white/8 pt-6 flex flex-wrap items-baseline gap-x-8 gap-y-3 text-xs text-slate-500">
                    <div>
                      <span className="text-slate-500">Email delivery: </span>
                      <span className="text-slate-200 font-medium">
                        {formData.email || "Not set"}
                      </span>
                    </div>
                    {formData.aiAssistant && (
                      <div>
                        <span className="text-slate-500">Tuned for: </span>
                        <span className="text-slate-200 font-medium">{formData.aiAssistant}</span>
                      </div>
                    )}
                    {formData.teamLiteracy && (
                      <div>
                        <span className="text-slate-500">Team posture: </span>
                        <span className="text-slate-200 font-medium">
                          {formData.teamLiteracy === "fast"
                            ? "Fast adopter"
                            : formData.teamLiteracy === "cautious"
                            ? "Cautious"
                            : "Change-resistant"}
                        </span>
                      </div>
                    )}
                    {prioritySupport && (
                      <div className="text-slate-200 font-medium">
                        ⚡ Priority support enabled
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Buttons */}
            <div className="flex justify-between items-center mt-6 pt-2">
              <Button
                variant="ghost"
                onClick={step === 1 ? () => navigate("/") : prevStep}
                className="text-slate-400 hover:text-white hover:bg-white/5 gap-1.5"
              >
                ← {step === 1 ? t("audit.buttonCancel") : t("audit.buttonBack")}
              </Button>

              {step < 4 && (
                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    onClick={nextStep}
                    style={{
                      background: `linear-gradient(135deg, ${accent.hex}, ${accent.hex}d0)`,
                      boxShadow: `0 8px 24px -8px ${accent.hex}80`,
                    }}
                    className="text-slate-950 font-bold px-7 gap-2 rounded-full transition-all hover:brightness-110"
                  >
                    {t("audit.buttonContinue")}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </motion.div>
              )}

              {step === 4 && (
                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="bg-gradient-to-br from-[#3b82f6] to-blue-500 hover:from-blue-500 hover:to-blue-700 text-slate-950 px-8 font-bold gap-2 rounded-full"
                  >
                    <Zap className="w-4 h-4" />
                    {t("audit.buttonGenerate")}
                  </Button>
                </motion.div>
              )}
            </div>

          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AuditFormPage;